// Castintech dashboard — Client OS (tenant-scoped panel) — Deliverable 5 Faz 2.
// Vault-kaynaklı: GET /castintech-api/client-os/overview?tenant=<slug>. gb-* bileşenleri yeniden kullanılır.
// Modül görünürlüğü = subscribed_products. Tenant'a kapalı veriler endpoint'te zaten filtrelenir.
import { apiFetch } from '../api.js';
import { escHtml, escAttr } from '../utils.js';
import { mesajlarKartiHtml, mesajAkisiHtml, cokMarkali, type PortalKonusma, type PortalKonusmaBaslik, type PortalMesaj } from './client-os-mesajlar.js';
import { firmaOzetiKartiHtml, type FirmaOzeti as SosyalMedyaOzeti } from '../omnichannel/firma-ozeti-gorunum.js';
import { renderLoading, renderError } from '../ui/state-display.js';
import { kartAktiviteleri, benzersizIsler } from './client-os-hizmet-eslesme.js';

interface Item { slug: string; title: string; summary?: string; state?: string; ptype?: string; }
interface Tenant {
  slug: string; display_name: string; relationship_type: string; tenant_type: string;
  status: string; sector: string; domain: string; strategic_role: string; summary: string;
}
interface Invoice { number: string; amount: number; currency: string; status: string; due_date?: string; sent_at?: string; paid_at?: string;
  /** Sunucu işaretledi: kayıt çelişkili/şema dışı → açık bakiyeye GİRMEDİ, teyit ediliyor. */
  bekliyor?: boolean; teyit?: string; }
interface Retainer { model: string; amount: number; currency: string; services: string; status: string; }
/** Piyasa karşılığı — sunucu yalnız gösterime uygunsa gönderir (bkz. index.ts kapıları). */
interface Benchmark { amount: number; currency: string; savings: number; note: string; }
interface Finance { balance_open: number; currency: string; count: number; invoices: Invoice[]; retainer?: Retainer; benchmark?: Benchmark; finance_display?: string;
  /** Açık bakiye para birimi başına. Birimler TOPLANMAZ (100 TRY + 10 USD ≠ 110). */
  balances?: Record<string, number>;
  /** Açık bakiyenin muhasebe kaydıyla eşleşmeyen kısmı — güncel ödeme teyidi yok. */
  balances_unconfirmed?: Record<string, number>;
  /** Teyit bekleyen kayıtların toplamı — borç DEĞİL. */
  pending_confirmation?: Record<string, number>;
  /** true → bazı kayıtlar okunamadı; "borç yok" sonucu çıkarılamaz. */
  finance_incomplete?: boolean;
  /**
   * true → PERSONEL önizlemesi (`?tenant=`): kayıt sistemi bu görünümde okunmaz, tutarlar yalnız
   * dosya kayıtlarından gelir. Müşteri oturumunda sunucu bu alanı GÖNDERMEZ. 29.09'da gerçek
   * tarayıcı kabulünde ölçüldü: aynı müşteri müşteri oturumunda 5.000 TRY, personel
   * önizlemesinde 125.000 TRY açık bakiye gösteriyordu (ödenmiş fatura borç sayılıyordu) ve
   * hiçbir işaret yoktu.
   */
  finance_preview_only?: boolean; }
interface AdsMetrics { spend?: number; clicks?: number; impressions?: number; conversions?: number; ctr?: number; avg_cpc?: number; impression_share?: number; }
interface AdsCampaign extends AdsMetrics { name: string; status?: string; prev?: AdsMetrics; }
interface AdsTotals { spend: number; clicks: number; impressions: number; conversions: number; }
interface AdsSeriesPoint { day: string; spend: number; clicks: number; impressions: number; conversions: number; ctr?: number; avg_cpc?: number; }
interface AdsChannel { period?: { start: string; end: string } | null; audit_date?: string; totals: AdsTotals; campaigns: AdsCampaign[]; prevPeriod?: { start: string; end: string } | null; prevTotals?: AdsTotals | null; series?: AdsSeriesPoint[]; label?: string; }
interface Ads { google?: AdsChannel; meta?: AdsChannel; google_secondary?: AdsChannel; }
interface MirrorInvoice { documentNo: string; totalAmount: number; currency: string; status: string | null; dueDate: string | null; paidAt: string | null; daysOverdue: number | null; }
interface MirrorFinance { invoices: MirrorInvoice[]; balanceOpen: number; overdueCount: number; currency: string; count: number; }
interface MirrorCrm { stage: string | null; openOpportunities: { name: string; stage: string | null; nextActionDue: string | null }[]; pendingDeliveries: { deliverable: string; status: string | null }[]; openTickets: { severity: string | null; status: string | null; sla: string | null }[]; }
interface ClientOsMirror { enabled: boolean; mapped: boolean; castacc: MirrorFinance | null; crm: MirrorCrm | null; }
interface SupportItem { id: string; subject: string; category: string; customer_status: string; created_at?: string; }
interface ServiceActivity { service: string; title: string; date: string; summary: string; status: 'done' | 'planned'; }
// CA-KAYIT-004 — iş kaydı kaynağının durumu. Alan YOKSA (eski sunucu yanıtı)
// undefined kalır ve hiçbir uyarı çizilmez → mevcut davranış aynen korunur.
type WorkRecordsStatus = 'ok' | 'empty' | 'unavailable' | 'not_applicable';
interface AccountLogEntry { title: string; date: string; summary: string; items: string[]; }
interface ClientTask { title: string; summary: string; status: string; done: boolean; }
interface ClientDelivery { ref: string; title: string; summary: string; link: string; status: string; clientApproved: boolean; officiallyAccepted: boolean; }
interface ClientPurchase { id: string; productCode: string; productName: string; purchasedAt: string; amount: number; currency: string; paymentStatus: string; paymentRaw: string; dueDate: string | null; closedAt: string | null; daysOverdue: number | null; }
interface ClientQuote { ref: string; title: string; stage: string; stageRaw: string; nextAction: string | null; amount: number | null; currency: string; kind: string; }
// K5/K6 — teklif aşaması panosu. Müşteri paneliyle AYRI nesne: teklif
// aşamasındaki kişi müşteri değil, ona hizmet kartı/finans/aylık rapor gösterilmez.
interface TeklifGorusme { ilkTemas: string | null; kanal: string; ozet: string }
interface TeklifAnaliz { baslik: string; ozet: string; link: string }
interface TeklifPano {
  durum: 'proposed' | 'frozen';
  teklifler: ClientQuote[];
  gorusme: TeklifGorusme | null;
  analiz: TeklifAnaliz | null;
  sonrakiAdim: string;
  dondurma: { zaman: string; sebep: string } | null;
}
// Panel 2.0 P1 — hizmet kataloğu (tenants.metadata.services) + kampanya açıklamaları.
interface ServiceDef { key: string; title: string; description?: string; domain?: string; channels?: string[]; status?: string; started_at?: string; }
interface CampaignNote { match: string; label: string; note: string; }
interface Overview { ok: boolean; mod?: string; teklif?: TeklifPano | null; tenant: Tenant; subscriptions: string[]; services?: ServiceDef[] | null; campaign_notes?: CampaignNote[] | null; report_pdf?: { ym: string; generated_at: string } | null; support_since?: string; routine_services?: string[] | null; projects: Item[]; finance?: Finance | null; ads?: Ads | null; mirror?: ClientOsMirror | null; support?: SupportItem[] | null; mesajlar?: PortalKonusma[] | null; sosyalMedyaOzeti?: SosyalMedyaOzeti | null; activity?: ServiceActivity[] | null; work_records_status?: WorkRecordsStatus | null; account_log?: AccountLogEntry[] | null; tasks?: ClientTask[] | null; deliveries?: ClientDelivery[] | null; purchases?: ClientPurchase[] | null; quotes?: ClientQuote[] | null; monthly_report?: MonthlyReport | null; satisfaction?: Satisfaction | null; viewer?: { kind: string; role: string | null; can: { finance: boolean; ads: boolean; overview: boolean } }; }

type VS = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; data: Overview };
let vs: VS = { kind: 'loading' };
interface Business { slug: string; name: string; parent_group: string | null; status: string; is_current: boolean; }
let coBusinesses: Business[] = [];
let coStaffView = false;
(window as unknown as { __coSwitchBusiness?: (s: string) => Promise<void> }).__coSwitchBusiness = async (s: string) => {
  if (!s) return;
  if (coStaffView) { location.href = '/dashboard?tenant=' + encodeURIComponent(s) + '#client-os'; return; }
  try {
    const r = await apiFetch('/castintech-api/client-os/switch-business', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenant: s }) });
    if (r.ok) location.href = '/dashboard#client-os';
  } catch { /* yut */ }
};

// F1 — iş-detay drill (K3 açıklayıcı alt sayfa). null = işletme paneli (K2) görünümü.
let coSelectedProject: string | null = null;
(window as unknown as { __coOpenProject?: (s: string) => void }).__coOpenProject = (s: string) => {
  coSelectedProject = s; renderClientOs();
  try { document.getElementById('view-client-os')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* yok say */ }
};
(window as unknown as { __coBackToList?: () => void }).__coBackToList = () => { coSelectedProject = null; renderClientOs(); };
// Faz 3 — teslimat onay SİNYALİ (signal-only; resmi kabul Yılmaz'da). POST → re-render.
(window as unknown as { __coApproveDelivery?: (ref: string) => Promise<void> }).__coApproveDelivery = async (ref: string) => {
  try {
    const r = await apiFetch(`/castintech-api/client-os/deliveries/${encodeURIComponent(ref)}/approve`, { method: 'POST' });
    if (r.ok) renderClientOs();
  } catch { /* sessiz */ }
};

/**
 * `?tenant=` URL paramı — PERSONEL önizlemesi için. Müşteri oturumunda BOŞ döner: kiracıyı
 * sunucu oturumdan çözer.
 *
 * Ölçüm (29.09.2026, gerçek tarayıcı): burada varsayılan `'kalder'` vardı. Müşteri kendi
 * oturumuyla paneli açtığında URL'de parametre olmadığı için istek `?tenant=kalder` gidiyor,
 * ürün doğru davranıp 403 "bu tenanta erisim yetkiniz yok" dönüyor ve müşteri kendi ekranı
 * yerine hata kutusu görüyordu. Sunucu `?tenant` YOKKEN oturumun kiracısını kullanır
 * (`tests/finans/panel-http-pg.test.js` HTTP01 bunu ölçüyor), bu yüzden doğru davranış
 * parametreyi hiç göndermemektir.
 */
export function currentTenant(): string {
  try { return (new URLSearchParams(location.search).get('tenant') || '').toLowerCase().replace(/[^a-z0-9-]/g, ''); }
  catch { return ''; }
}

/** `?tenant=<slug>` ya da (müşteri oturumu) BOŞ metin — sorgu dizesine eklenmek üzere. */
export function tenantSorgusu(slug = currentTenant()): string {
  return slug ? `tenant=${encodeURIComponent(slug)}` : '';
}

export async function loadClientOs(): Promise<void> {
  vs = { kind: 'loading' };
  coSelectedProject = null; // yeni yükleme = panel görünümü
  coMsgThread = null; coMsgHata = '';
  const slug = currentTenant();
  try {
    const r = await apiFetch(`/castintech-api/client-os/overview?${tenantSorgusu(slug)}`);
    const j = await r.json().catch(() => ({ ok: false, error: 'geçersiz yanıt' }));
    if (!r.ok || !j.ok) { vs = { kind: 'error', message: j.error || ('HTTP ' + r.status) }; return; }
    try {
      const mr = await apiFetch(`/castintech-api/client-os/castacc-mirror?${tenantSorgusu(slug)}`);
      const mj = await mr.json().catch(() => null);
      (j as Overview).mirror = (mj && mj.ok) ? mj as ClientOsMirror : null;
    } catch { (j as Overview).mirror = null; }
    try {
      const sr = await apiFetch(`/castintech-api/client-os/support/requests`);
      const sj = await sr.json().catch(() => null);
      (j as Overview).support = (sj && sj.ok && Array.isArray(sj.requests)) ? sj.requests as SupportItem[] : [];
    } catch { (j as Overview).support = []; }
    try {
      // WP3 dilim 2 — kanal konuşmaları. Bayrak kapalıyken uç 404 → null → kart hiç çizilmez.
      const ir = await apiFetch(`/castintech-api/client-os/inbox/conversations?${tenantSorgusu(slug)}`);
      const ij = await ir.json().catch(() => null);
      (j as Overview).mesajlar = (ir.ok && ij && ij.ok && Array.isArray(ij.konusmalar)) ? ij.konusmalar as PortalKonusma[] : null;
    } catch { (j as Overview).mesajlar = null; }
    try {
      // Faz 6b — sosyal medya iletişim özeti. Bayrak kapalıyken uç 404 → null → kart hiç çizilmez.
      const or_ = await apiFetch(`/castintech-api/client-os/sosyal-medya/ozet?${tenantSorgusu(slug)}`);
      const oj = await or_.json().catch(() => null);
      (j as Overview).sosyalMedyaOzeti = (or_.ok && oj && oj.ok && Array.isArray(oj.markalar)) ? { markalar: oj.markalar, toplam: oj.toplam } as SosyalMedyaOzeti : null;
    } catch { (j as Overview).sosyalMedyaOzeti = null; }
    try {
      const bres = await apiFetch('/castintech-api/client-os/businesses?tenant=' + encodeURIComponent(slug));
      const bj = await bres.json().catch(() => null);
      if (bj && bj.ok && Array.isArray(bj.businesses)) { coBusinesses = bj.businesses as Business[]; coStaffView = !!bj.staff; }
    } catch { coBusinesses = []; }
    vs = { kind: 'ready', data: j as Overview };
  } catch (e) { vs = { kind: 'error', message: (e as Error).message }; }
}

export function renderClientOs(): void {
  const root = document.getElementById('view-client-os');
  if (!root) return;
  if (vs.kind === 'loading') { renderLoading(root, { text: 'Portal yükleniyor…' }); return; }
  if (vs.kind === 'error') { renderError(root, vs.message, { onRetry: () => { void loadClientOs().then(renderClientOs); } }); return; }
  // Müşteri oturumunda iç-panel dönüş butonu gizlenir (staff önizlemesinde kalır).
  try { const cb = document.getElementById('co-back'); if (cb) cb.style.display = vs.data.viewer?.kind === 'tenant' ? 'none' : ''; } catch { /* yok say */ }
  // K3 iş-detay alt sayfası (seçili proje varsa), aksi K2 işletme paneli.
  if (coSelectedProject) {
    const p = vs.data.projects.find((x) => x.slug === coSelectedProject);
    if (p) { root.innerHTML = projectDetailHtml(vs.data, p); return; }
    coSelectedProject = null; // bulunamadı → panele düş
  }
  root.innerHTML = html(vs.data);
}

// ── Modül kataloğu — subscription slug → Client OS modülü ──
const MODULES: Record<string, { icon: string; label: string; note: string }> = {
  'castagent': { icon: '🧠', label: 'Operasyon', note: 'Görev, karar ve onay akışı' },
  'client-os': { icon: '🪟', label: 'Müşteri Paneli', note: 'Bu panel' },
  'castacc': { icon: '🧾', label: 'Finans / Cari', note: 'Cari, fatura, ödeme — yakında (Faz 5)' },
  'castmart': { icon: '🛒', label: 'E-ticaret', note: 'Ürün, sipariş, satış — yakında (Faz 6)' },
  'castdesign': { icon: '🎨', label: 'Tasarım', note: 'Web/prototip/revizyon işleri' },
  'lead-generation': { icon: '🎯', label: 'Lead Generation', note: 'Lead funnel — yakında (Faz 7)' },
  'ads-management': { icon: '📣', label: 'Reklam', note: 'Google + Meta reklam yönetimi' },
  'google-ads': { icon: '🔎', label: 'Google Ads', note: 'Arama/yerel reklam yönetimi' },
  'meta-ads': { icon: '📣', label: 'Meta Ads', note: 'Instagram/Facebook reklam' },
  'web-management': { icon: '🌐', label: 'Web / SEO', note: 'Site + SEO görünürlüğü' },
  'seo': { icon: '🔎', label: 'SEO', note: 'Arama görünürlüğü' },
  'sosyal-medya': { icon: '📱', label: 'Sosyal Medya', note: 'İçerik + paylaşım yönetimi' },
  'icerik': { icon: '🎬', label: 'İçerik Üretimi', note: 'Görsel + video + metin' },
  'web-sitesi': { icon: '🌐', label: 'Web Sitesi', note: 'Çok dilli kurumsal web siteniz' },
  'aeo': { icon: '💬', label: 'Cevap Motoru (AEO)', note: 'Arama cevap kutularında görünürlük' },
  'aio-geo': { icon: '🤖', label: 'Yapay Zekâ Görünürlüğü (AIO/GEO)', note: 'ChatGPT, Gemini gibi asistanlarda doğru bilgi' },
  'analitik': { icon: '📊', label: 'Ölçüm & Takip', note: 'Ziyaretçi + dönüşüm analitiği' },
};

const TYPE_TONE: Record<string, string> = { internal_company: 'emerald', institutional_partner: 'sapphire', external_customer: 'sapphire', product_unit: 'teal', prospect: 'amber' };

function pill(text: string, tone = 'sapphire'): string { return `<span class="gb-pill gb-pill-${tone}">${escHtml(text)}</span>`; }

// genel-bakis ile aynı .gb-tile markup'ı (mevcut CSS yeniden kullanılır).
function kpiTile(icon: string, label: string, value: number | string, color: string): string {
  return `<div class="gb-tile" style="--gb-accent:${color}">
    <div class="gb-tile-icon">${icon}</div>
    <div class="gb-tile-body"><div class="gb-tile-value">${escHtml(String(value))}</div><div class="gb-tile-label">${escHtml(label)}</div></div>
  </div>`;
}

// ── 11-state proje yaşam döngüsü izleyici ([[customer-project-lifecycle]]) ──
const STATES = ['intake', 'discovery', 'proposal', 'demo', 'accepted', 'planning', 'execution', 'review', 'delivered', 'support'] as const;
const STATE_LABEL: Record<string, string> = {
  intake: 'Talep', discovery: 'Keşif', proposal: 'Teklif', demo: 'Demo', accepted: 'Onaylandı',
  planning: 'Planlama', execution: 'Yürütme', review: 'İnceleme', delivered: 'Teslim', support: 'Destek', cancelled: 'İptal',
};
// Bekleyen taraf: bu state'lerde top müşteride; diğerlerinde bizde.
const WAIT_CUSTOMER = new Set(['proposal', 'demo', 'accepted', 'review']);
function waitBadge(state: string): string {
  if (state === 'delivered' || state === 'support') return `<span class="gb-pill gb-pill-emerald">tamam</span>`;
  if (state === 'cancelled') return `<span class="gb-pill gb-pill-muted">iptal</span>`;
  return WAIT_CUSTOMER.has(state)
    ? `<span class="gb-pill gb-pill-amber">sizden bekleniyor</span>`
    : `<span class="gb-pill gb-pill-sapphire">ekibinizde</span>`;
}
function projectTracker(p: Item): string {
  const st = (p.state || 'intake').toLowerCase();
  const cur = STATES.indexOf(st as any);
  const steps = STATES.map((s, i) => {
    const cls = st === 'cancelled' ? 'co-step' : i < cur ? 'co-step done' : i === cur ? 'co-step cur' : 'co-step';
    return `<div class="${cls}" title="${escAttr(STATE_LABEL[s] || s)}"><span class="co-step-dot"></span><span class="co-step-lbl">${escHtml(STATE_LABEL[s] || s)}</span></div>`;
  }).join('<span class="co-step-line"></span>');
  const typeChip = p.ptype ? `<span class="gb-pill gb-pill-muted">${escHtml(p.ptype)}</span>` : '';
  const open = `window.__coOpenProject&&window.__coOpenProject('${escAttr(p.slug)}')`;
  return `<div class="co-proj co-proj-click" role="button" tabindex="0" aria-label="${escAttr(p.title)} — detayı aç"
      onclick="${open}" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();${open}}">
    <div class="co-proj-head"><div class="co-proj-title">${escHtml(p.title)} <span class="co-proj-arrow" aria-hidden="true">›</span></div>
      <div class="co-proj-meta">${typeChip}<span class="gb-pill gb-pill-sapphire">${escHtml(STATE_LABEL[st] || st)}</span>${waitBadge(st)}</div></div>
    ${p.summary ? `<div class="co-proj-sum">${escHtml(p.summary)}</div>` : ''}
    <div class="co-steps">${steps}</div>
  </div>`;
}
function projectsCard(items: Item[]): string {
  if (!items.length) return `<div class="gb-card gb-co-projwrap"><div class="gb-card-title">Projeler / İşler</div><div class="gb-empty-sm">Henüz açık iş yok — ekibiniz bir iş başlattığında burada görünecek.</div></div>`;
  return `<div class="gb-card gb-co-projwrap"><div class="gb-card-title">Projeler / İşler <span class="gb-card-sub">${items.length}</span> <span class="co-hint">— detay için bir işe tıklayın</span></div>
    ${items.map(projectTracker).join('')}</div>`;
}

// ── K3 — İş / Proje detayı (açıklayıcı alt sayfa). GERÇEK veri + dürüst yönlendirici boş-durum. ──
function projectDetailHtml(d: Overview, p: Item): string {
  const t = d.tenant;
  const st = (p.state || 'intake').toLowerCase();
  const cur = STATES.indexOf(st as any);
  const steps = STATES.map((s, i) => {
    const cls = st === 'cancelled' ? 'co-step' : i < cur ? 'co-step done' : i === cur ? 'co-step cur' : 'co-step';
    return `<div class="${cls}" title="${escAttr(STATE_LABEL[s] || s)}"><span class="co-step-dot"></span><span class="co-step-lbl">${escHtml(STATE_LABEL[s] || s)}</span></div>`;
  }).join('');
  const crumb = `<nav class="co-crumb" aria-label="Konum">
    <a href="#" onclick="event.preventDefault();window.__coBackToList&&window.__coBackToList()">İşlerim</a>
    <span class="co-crumb-sep">/</span><span>${escHtml(t.display_name)}</span>
    <span class="co-crumb-sep">/</span><b>${escHtml(p.title)}</b></nav>`;
  const typeChip = p.ptype ? `<span class="gb-pill gb-pill-muted">${escHtml(p.ptype)}</span>` : '';
  const waiting = WAIT_CUSTOMER.has(st);
  const callout = waiting
    ? `<div class="co-callout co-callout-warn">⚠︎ <b>Sizden bekleniyor</b> — bu aşamada işin ilerlemesi için onayınızı / geri bildiriminizi bekliyoruz.</div>`
    : (st === 'delivered' || st === 'support')
      ? `<div class="co-callout co-callout-ok">✓ Bu iş tamamlandı — teslim edildi.</div>`
      : (st === 'cancelled')
        ? `<div class="co-callout co-callout-mut">Bu iş iptal edildi.</div>`
        : `<div class="co-callout">Ekibiniz bu iş üzerinde çalışıyor. Bir aksiyon gerektiğinde burada bildirilir.</div>`;
  return `${crumb}
  <div class="gb-card co-detail">
    <button class="co-back-btn" onclick="window.__coBackToList&&window.__coBackToList()">← İşlere dön</button>
    <div class="co-detail-head">
      <div class="co-detail-title">${escHtml(p.title)}</div>
      <div class="co-proj-meta">${typeChip}<span class="gb-pill gb-pill-sapphire">${escHtml(STATE_LABEL[st] || st)}</span>${waitBadge(st)}</div>
    </div>
    ${callout}
    <div class="co-sec"><div class="co-sec-title">İlerleme</div><div class="co-steps">${steps}</div></div>
    ${p.summary ? `<div class="co-sec"><div class="co-sec-title">Açıklama</div><div class="co-detail-sum">${escHtml(p.summary)}</div></div>` : ''}
    <div class="co-sec"><div class="co-sec-title">Teslimatlar</div><div class="gb-empty-sm">Bu işin teslimat kalemleri, iş ilerledikçe hesap ekibiniz tarafından burada listelenecek.</div></div>
    <div class="co-sec"><div class="co-sec-title">Ekli Dosyalar</div><div class="gb-empty-sm">Çıktı ve referans dosyaları hazır olduğunda burada görünecek.</div></div>
    <div class="co-detail-foot">🛰️ Hesap yöneticiniz (GM ajanınız) bu işi yürütüyor. Soru veya isteğiniz için <b>Destek Talepleri</b>'nden yazabilirsiniz — talebiniz doğrudan ekibinize düşer.</div>
  </div>`;
}

// ── Faz K: Aylık Performans Karnesi — Excel raporlama standardının portal hali ──
interface MonthlyMetric { key: 'spend' | 'clicks' | 'impressions' | 'conversions' | 'ctr' | 'cpc'; cur: number | null; prev: number | null; deltaPct: number | null; yoy: number | null; yoyPct: number | null; }
interface MTrendPoint { ym: string; spend: number; clicks: number; impressions: number; conversions: number; }
interface MonthlyChannel { key: string; label: string; curDays: number; prevDays: number; prevPartial: boolean; metrics: MonthlyMetric[]; hasYoy: boolean; trend: MTrendPoint[]; }
interface OrganicMetric { key: string; cur: number | null; prev: number | null; deltaPct: number | null; yoy: number | null; yoyPct: number | null; }
interface OrganicChannel { key: string; label: string; curDays: number; prevDays: number; metrics: OrganicMetric[]; trend: Array<{ ym: string; values: Record<string, number> }>; hasYoy: boolean; }
interface MonthlyReport { current: string; prev: string; yearAgo: string; channels: MonthlyChannel[]; organic?: OrganicChannel[] | null; pending: string[]; monthsSpan: number; }

const TR_AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
function ymTr(ym: string): string { const [y, m] = ym.split('-').map(Number); return `${TR_AY[(m || 1) - 1]} ${y}`; }

// Her metrik: müşteri-dostu ad + sade açıklama (Excel AÇIKLAMA sütunu standardı) + iyi-yön + format.
// Açıklamalar xlsx rapor standardının (Marka Asimetri şablonu) tam tanımlarıyla hizalı.
const MREP_INFO: Record<MonthlyMetric['key'], { label: string; desc: string; dir: -1 | 0 | 1; fmt: 'tl' | 'tl2' | 'int' | 'pct' }> = {
  spend: { label: 'Harcama', desc: 'Bu ay reklamlar için kullanılan toplam bütçe.', dir: 0, fmt: 'tl' },
  clicks: { label: 'Tıklama', desc: 'Reklamı görüp sitenize gelen kişi sayısı — ilgiyi gösterir.', dir: 1, fmt: 'int' },
  impressions: { label: 'Gösterim', desc: 'Reklamınızın bir kullanıcının ekranında göründüğü toplam sayı; aynı kullanıcı birden fazla kez görse her seferi ayrı sayılır (tekrarlı sayım) — bilinirliği gösterir.', dir: 1, fmt: 'int' },
  conversions: { label: 'Dönüşüm', desc: 'Tıklama sonrası gerçekleşen değerli işlem (teklif formu, arama, sipariş) — asıl hedef budur.', dir: 1, fmt: 'int' },
  ctr: { label: 'Tıklama Oranı (CTR)', desc: 'Reklamın toplam gösterimine karşılık kaç kez tıklandığının yüzdesi. Yüksek CTR reklam metni/görseli/hedeflemenin ilgi çekici olduğunu, düşük CTR yeterince dikkat çekmediğini veya yanlış kitleye gösterildiğini işaret eder.', dir: 1, fmt: 'pct' },
  cpc: { label: 'Tıklama Başına Maliyet', desc: 'Sitenize gelen her ziyaretçi için ödenen ortalama tutar — düşük olması verimliliktir.', dir: -1, fmt: 'tl2' },
};
function mrepFmt(v: number | null, fmt: string): string {
  if (v == null) return '—';
  if (fmt === 'tl') return '₺' + Math.round(v).toLocaleString('tr-TR');
  if (fmt === 'tl2') return '₺' + v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (fmt === 'pct') return '%' + v.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
  return v.toLocaleString('tr-TR');
}
function mrepDelta(m: MonthlyMetric): string {
  const info = MREP_INFO[m.key];
  if (m.deltaPct == null) return '<span class="co-mrep-na">—</span>';
  const up = m.deltaPct > 0, flat = m.deltaPct === 0;
  const cls = flat || info.dir === 0 ? 'neu' : (up === (info.dir > 0) ? 'good' : 'bad');
  const arrow = flat ? '≈' : up ? '▲' : '▼';
  return `<span class="co-mrep-delta co-mrep-${cls}">${arrow} %${Math.abs(m.deltaPct).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</span>`;
}
// Excel çok-aylı: bir değişim-yüzdesini iyi-yön renkleriyle göster.
function mrepDeltaCell(deltaPct: number | null, dir: -1 | 0 | 1): string {
  if (deltaPct == null) return '<span class="co-mrep-na">—</span>';
  const up = deltaPct > 0, flat = deltaPct === 0;
  const cls = flat || dir === 0 ? 'neu' : (up === (dir > 0) ? 'good' : 'bad');
  const arrow = flat ? '≈' : up ? '▲' : '▼';
  return `<span class="co-mrep-delta co-mrep-${cls}">${arrow} %${Math.abs(deltaPct).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</span>`;
}
// Son 12 ay trend tablosu (harcama/tıklama) + ardışık ay-çifti % satırları
// (xlsx standardının "Şubat-Ocak %, Mart-Şubat %…" matrisi karşılığı).
function mrepTrend(ch: MonthlyChannel): string {
  if (ch.trend.length < 2) return '';
  const head = ch.trend.map((t) => `<th>${escHtml(ymTr(t.ym).replace(/ 20/, ' ’'))}</th>`).join('');
  const rowFor = (lbl: string, fmt: (t: MTrendPoint) => string) => `<tr><td class="co-mtr-lbl">${lbl}</td>${ch.trend.map((t) => `<td>${fmt(t)}</td>`).join('')}</tr>`;
  const momCell = (cur: number, prev: number | null, dir: -1 | 0 | 1): string => {
    if (prev == null) return '<td><span class="co-mrep-na">—</span></td>';
    const d = prev === 0 ? (cur === 0 ? 0 : null) : ((cur - prev) / prev) * 100;
    return `<td>${mrepDeltaCell(d, dir)}</td>`;
  };
  const momRow = (lbl: string, key: 'spend' | 'clicks', dir: -1 | 0 | 1) =>
    `<tr class="co-mtr-mom"><td class="co-mtr-lbl">${lbl} Δ%</td>${ch.trend.map((t, i) => momCell(t[key], i > 0 ? ch.trend[i - 1][key] : null, dir)).join('')}</tr>`;
  return `<details class="co-mtr"><summary>📈 Son 12 ayın seyri (${ch.trend.length} ay) — ay-ay değişim yüzdeleriyle</summary>
    <div class="co-mrep-tblwrap"><table class="gb-table co-mtr-tbl"><thead><tr><th></th>${head}</tr></thead><tbody>
      ${rowFor('Harcama', (t) => '₺' + t.spend.toLocaleString('tr-TR'))}
      ${momRow('Harcama', 'spend', 0)}
      ${rowFor('Tıklama', (t) => t.clicks.toLocaleString('tr-TR'))}
      ${momRow('Tıklama', 'clicks', 1)}
    </tbody></table></div></details>`;
}
function monthlyReportCard(mr: MonthlyReport | null | undefined, slug: string, rp?: { ym: string; generated_at: string } | null): string {
  if (!mr || (!mr.channels.length && !(mr.organic || []).length)) return '';
  const curL = ymTr(mr.current), prevL = ymTr(mr.prev);
  const yaL = ymTr(mr.yearAgo);
  // P2 — ÖZETLER: tüm kanallar tek bakış (xlsx 'ÖZETLER' sayfası karşılığı).
  const mAt = (ch: MonthlyChannel, k: string) => ch.metrics.find((m) => m.key === (k as MonthlyMetric['key']));
  const sumRows = mr.channels.map((ch) => {
    const sp = mAt(ch, 'spend'), cl = mAt(ch, 'clicks'), im = mAt(ch, 'impressions');
    return `<tr><td class="co-mrep-met"><b>${escHtml(ch.label)}</b></td>
      <td class="co-mrep-val">${sp ? mrepFmt(sp.cur, 'tl') : '—'} ${sp ? mrepDeltaCell(sp.deltaPct, 0) : ''}</td>
      <td class="co-mrep-val">${cl ? mrepFmt(cl.cur, 'int') : '—'} ${cl ? mrepDeltaCell(cl.deltaPct, 1) : ''}</td>
      <td class="co-mrep-val">${im ? mrepFmt(im.cur, 'int') : '—'} ${im ? mrepDeltaCell(im.deltaPct, 1) : ''}</td></tr>`;
  }).join('');
  const ozet = mr.channels.length > 1
    ? `<div class="co-mrep-ch"><div class="co-mrep-chhead"><b>📋 Özet — Tüm Kanallar</b><span class="co-mrep-chip">${escHtml(curL)} · aylık değişimle</span></div>
      <div class="co-mrep-tblwrap"><table class="gb-table co-mrep-tbl"><thead><tr><th>Kanal</th><th>Harcama</th><th>Tıklama</th><th>Gösterim</th></tr></thead><tbody>${sumRows}</tbody></table></div></div>`
    : '';
  const blocks = mr.channels.map((ch) => {
    const yoyTh = ch.hasYoy ? `<th>${escHtml(yaL)}</th><th>Yıllık</th>` : '';
    // Dönüşüm satırı geçici gizli: ölçüm hijyeni (GA4/Meta aksiyon karışımı)
    // düzelene dek müşteriye yanıltıcı dönüşüm rakamı gösterilmez.
    const rows = ch.metrics.filter((m) => m.key !== 'conversions').map((m) => {
      const info = MREP_INFO[m.key];
      const yoyTd = ch.hasYoy ? `<td class="co-mrep-val">${mrepFmt(m.yoy, info.fmt)}</td><td class="co-mrep-val">${mrepDeltaCell(m.yoyPct, info.dir)}</td>` : '';
      return `<tr><td class="co-mrep-met"><div class="co-mrep-lbl">${escHtml(info.label)}</div><div class="co-mrep-desc">${escHtml(info.desc)}</div></td>
        <td class="co-mrep-val"><b>${mrepFmt(m.cur, info.fmt)}</b></td>
        <td class="co-mrep-val">${mrepFmt(m.prev, info.fmt)}</td>
        <td class="co-mrep-val">${mrepDeltaCell(m.deltaPct, info.dir)}</td>${yoyTd}</tr>`;
    }).join('');
    const prevChip = ch.prevDays
      ? `<span class="co-mrep-chip">${escHtml(prevL)}: ${ch.prevDays} gün${ch.prevPartial ? ' · kısmi veri' : ''}</span>`
      : `<span class="co-mrep-chip">${escHtml(prevL)}: veri yok</span>`;
    return `<div class="co-mrep-ch"><div class="co-mrep-chhead"><b>${escHtml(ch.label)}</b>
        <span class="co-mrep-chip">${escHtml(curL)}: ${ch.curDays} gün</span>${prevChip}${ch.hasYoy ? '<span class="co-mrep-chip co-mrep-chip-y">yıllık kıyas hazır</span>' : ''}</div>
      <div class="co-mrep-tblwrap"><table class="gb-table co-mrep-tbl"><thead><tr><th>Ne ölçüyoruz?</th><th>${escHtml(curL)} (bu ay)</th><th>${escHtml(prevL)}</th><th>Aylık</th>${yoyTh}</tr></thead><tbody>${rows}</tbody></table></div>
      ${mrepTrend(ch)}</div>`;
  }).join('');
  // P2 — organik kanallar (Instagram/Facebook/Web): xlsx standardının sosyal+web sayfaları.
  // Metrik tanımları xlsx AÇIKLAMA sütunundan (erişim=benzersiz, görüntülenme=tekrarlı sayım).
  const ORG_INFO: Record<string, { label: string; desc: string; fmt: 'int' }> = {
    followers: { label: 'Takipçi', desc: 'Hesabınızı takip eden toplam kişi sayısı (ay sonu değeri).', fmt: 'int' },
    reach: { label: 'Erişim', desc: 'İçeriklerinizin ulaştığı benzersiz kullanıcı sayısı — aynı kişi birden çok kez görse bile 1 sayılır.', fmt: 'int' },
    views: { label: 'Görüntülenme', desc: 'İçeriklerinizin ekranda görünme/izlenme toplamı (tekrarlı sayım).', fmt: 'int' },
    engaged: { label: 'Etkileşen Hesap', desc: 'İçeriklerinizle aktif etkileşim (beğeni, yorum, paylaşım, kaydetme) kuran hesap sayısı.', fmt: 'int' },
    page_views: { label: 'Sayfa Görüntülenme', desc: 'Facebook sayfanızın profil görüntülenme sayısı.', fmt: 'int' },
    sessions: { label: 'Oturum', desc: 'Web sitenizde başlatılan ziyaret oturumu sayısı.', fmt: 'int' },
    pageviews: { label: 'Sayfa Görüntülenme', desc: 'Sitenizdeki toplam sayfa görüntülenmesi.', fmt: 'int' },
    new_users: { label: 'Yeni Kullanıcı', desc: 'Sitenizi ilk kez ziyaret eden kullanıcı sayısı.', fmt: 'int' },
    events: { label: 'Etkinlik', desc: 'Sitede gerçekleşen etkileşim olayları (tıklama, form, kaydırma vb.).', fmt: 'int' },
    yeni_musteri: { label: 'Yeni Müşteri (Lead)', desc: 'WhatsApp hattınıza ilk kez yazan potansiyel müşteri sayısı.', fmt: 'int' },
    teklif: { label: 'Teklife Geçen', desc: 'Ay içinde kendisine fiyat/teklif verilen müşteri sayısı.', fmt: 'int' },
    satis: { label: 'Satış', desc: 'Ay içinde satışa dönüşen müşteri sayısı.', fmt: 'int' },
    acik_teklif: { label: 'Açık Teklif', desc: 'Teklif verilmiş, henüz sonuçlanmamış müşteri sayısı (ay sonu durumu).', fmt: 'int' },
    acik_teklif_tl: { label: 'Açık Tekliflerin Değeri (₺)', desc: 'Sonuçlanmamış tekliflerin yaklaşık toplam tutarı — mesajlardaki fiyatlardan otomatik hesaplanır.', fmt: 'int' },
    bekleyen_firsat: { label: 'Bekleyen Fırsat', desc: 'Görüşme veya teklif aşamasında olup kapanmamış müşteri sayısı (ay sonu durumu).', fmt: 'int' },
    ilk_yanit_dk: { label: 'İlk Yanıt Süresi (dk)', desc: 'Yeni müşteriye ilk cevabın dakika cinsinden tipik süresi (otomatik karşılama sayılmaz).', fmt: 'int' },
  };
  const ORG_ICON: Record<string, string> = { ig: '📸', fb: '👥', ga4: '🌐', wa: '💬' };
  const organicBlocks = (mr.organic || []).map((oc) => {
    const rows = oc.metrics.filter((m) => m.cur != null || m.prev != null).map((m) => {
      const info = ORG_INFO[m.key] || { label: m.key, desc: '', fmt: 'int' as const };
      const yoyTd = oc.hasYoy ? `<td class="co-mrep-val">${mrepFmt(m.yoy, info.fmt)}</td><td class="co-mrep-val">${mrepDeltaCell(m.yoyPct, 1)}</td>` : '';
      return `<tr><td class="co-mrep-met"><div class="co-mrep-lbl">${escHtml(info.label)}</div><div class="co-mrep-desc">${escHtml(info.desc)}</div></td>
        <td class="co-mrep-val"><b>${mrepFmt(m.cur, info.fmt)}</b></td>
        <td class="co-mrep-val">${mrepFmt(m.prev, info.fmt)}</td>
        <td class="co-mrep-val">${mrepDeltaCell(m.deltaPct, 1)}</td>${yoyTd}</tr>`;
    }).join('');
    if (!rows) return '';
    const yoyTh = oc.hasYoy ? `<th>${escHtml(yaL)}</th><th>Yıllık</th>` : '';
    const prevChip = oc.prevDays ? `<span class="co-mrep-chip">${escHtml(prevL)}: ${oc.prevDays} gün</span>` : `<span class="co-mrep-chip">${escHtml(prevL)}: veri yok</span>`;
    return `<div class="co-mrep-ch"><div class="co-mrep-chhead"><b>${ORG_ICON[oc.key] || '📊'} ${escHtml(oc.label)}</b>
        <span class="co-mrep-chip">${escHtml(curL)}: ${oc.curDays} gün</span>${prevChip}</div>
      <div class="co-mrep-tblwrap"><table class="gb-table co-mrep-tbl"><thead><tr><th>Ne ölçüyoruz?</th><th>${escHtml(curL)} (bu ay)</th><th>${escHtml(prevL)}</th><th>Aylık</th>${yoyTh}</tr></thead><tbody>${rows}</tbody></table></div></div>`;
  }).join('');
  const pending = mr.pending.length
    ? `<div class="co-mrep-pend"><div class="co-mrep-pendtitle">Rapor kapsamı genişliyor</div>
       <div class="co-mrep-pendchips">${mr.pending.map((p) => `<span class="co-mrep-pchip">${escHtml(p)}</span>`).join('')}</div>
       <div class="gb-card-sub">Bu kanallar bağlantıları tamamlandığında rapora otomatik eklenecek.</div></div>`
    : '';
  // P3 — PDF damgası: hangi ayın raporu + ne zaman üretildi; ay-içi anlık rapor ayrı.
  const pdfBtn = rp
    ? `<a class="co-mrep-pdf" href="/castintech-api/client-os/report.pdf?${tenantSorgusu(slug)}" target="_blank" rel="noopener" title="Üretim: ${escAttr(rp.generated_at.slice(0, 16).replace('T', ' '))}">📄 ${escHtml(ymTr(rp.ym))} Raporu (PDF)</a>`
    : `<span class="gb-card-sub" title="İlk aylık PDF, ay sonunda otomatik oluşturulur">📄 Aylık PDF ay sonunda hazırlanır</span>`;
  const nowBtn = `<a class="co-mrep-pdf" href="/castintech-api/client-os/report-current.pdf?${tenantSorgusu(slug)}" target="_blank" rel="noopener">📄 Anlık Rapor — bugüne kadar (PDF)</a>`;
  return `<div class="co-section-title co-mrep-titlerow">📊 Aylık Performans Karneniz ${pdfBtn} ${nowBtn}</div>
    <div class="gb-card-sub" style="margin:-6px 0 10px">${escHtml(curL)} sonuçlarınız, ${escHtml(prevL)} ile karşılaştırmalı. Her satırda o değerin ne anlama geldiğini sade dille açıkladık — bu ay henüz bitmediği için rakamlar gün geçtikçe artar.</div>
    <div class="gb-card co-mrep">${ozet}${blocks}${organicBlocks}${pending}
    <div class="gb-card-sub" style="margin-top:10px">ℹ️ ${mr.channels.some((c) => c.hasYoy) ? 'Yıllık kıyas, geçmiş verisi 12 ayı aşan kanallarda gösterilir.' : 'Yıllık kıyas, 12 aylık veri biriktiğinde otomatik eklenecek.'} Veriler doğrudan reklam platformlarından günlük alınır; geçmiş aylar da rapora dahildir.</div></div>`;
}

function modulesGrid(subs: string[]): string {
  const cards = subs.map(s => { const m = MODULES[s]; if (!m) return ''; return `<div class="gb-card gb-mod-card"><div class="gb-mod-ico">${m.icon}</div>
    <div><div class="gb-mod-label">${escHtml(m.label)}</div><div class="gb-mod-note">${escHtml(m.note)}</div></div></div>`; }).filter(Boolean).join('');
  return `<div class="gb-card gb-mod-wrap"><div class="gb-card-title">Ürünler & Hizmetler <span class="gb-card-sub">${subs.length} abonelik</span></div>
    <div class="gb-mod-grid">${cards || '<div class="gb-empty-sm">Abonelik yok</div>'}</div></div>`;
}

// sayı formatı (tr-TR)
function nf(n: number | undefined, d = 0): string { return (typeof n === 'number' ? n : 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d }); }
const INV_TONE: Record<string, string> = { paid: 'emerald', sent: 'sapphire', pending: 'amber', draft: 'muted', overdue: 'ruby', cancelled: 'muted' };


function mirrorCard(m: ClientOsMirror): string {
  if (!m || !m.enabled || !m.mapped) return '';
  let fin = '';
  if (m.castacc) {
    const f = m.castacc;
    const rows = f.invoices.slice(0, 20).map(iv => `<tr><td>${escHtml(iv.documentNo)}</td><td>${iv.totalAmount.toLocaleString('tr-TR')} ${escHtml(iv.currency)}</td><td>${iv.paidAt ? '✅ ödendi' : (iv.daysOverdue != null ? '🔴 ' + iv.daysOverdue + 'g gecikmiş' : '⏳ açık')}</td><td>${escHtml(iv.dueDate || '-')}</td></tr>`).join('');
    fin = `<div class="gb-card-sub">Muhasebe (Castacc) · açık bakiye <b>${f.balanceOpen.toLocaleString('tr-TR')} ${escHtml(f.currency)}</b> · ${f.overdueCount} gecikmiş fatura</div><table class="gb-table"><thead><tr><th>Fatura</th><th>Tutar</th><th>Durum</th><th>Vade</th></tr></thead><tbody>${rows || '<tr><td colspan="4">Fatura yok</td></tr>'}</tbody></table>`;
  }
  let crm = '';
  if (m.crm) {
    const c = m.crm;
    crm = `<div class="gb-card-sub" style="margin-top:10px">CRM durumu · aşama <b>${escHtml(c.stage || '-')}</b></div><ul class="co-crm"><li>Açık fırsat: <b>${c.openOpportunities.length}</b></li><li>Bekleyen teslim: <b>${c.pendingDeliveries.length}</b></li><li>Açık destek talebi: <b>${c.openTickets.length}</b></li></ul>`;
  }
  return `<div class="gb-card"><div class="gb-card-title">Finans &amp; CRM Durumu <span class="gb-card-sub">read-only ayna</span></div>${fin}${crm}</div>`;
}

function financeCard(f: Finance): string {
  const rows = f.invoices.map(iv => `<tr>
    <td class="gb-c-name">${escHtml(iv.number)}</td>
    <td>${nf(iv.amount, 2)} ${escHtml(iv.currency)}</td>
    <td><span class="gb-pill gb-pill-${INV_TONE[iv.status] || 'sapphire'}">${escHtml(iv.status || '—')}</span>${iv.bekliyor ? ' <span class="gb-pill gb-pill-amber" title="Kayıt kontrol ediliyor; açık bakiyeye eklenmedi">teyit ediliyor</span>' : ''}${
      // Açık ama muhasebe kaydıyla eşleşmemiş fatura: müşteri "ödedim" diyebilir ve haklı olabilir.
      // Teknik terim (`tarihsel-kayit`) DEĞİL, ne demek istediğimizi yazıyoruz.
      !iv.bekliyor && iv.teyit === 'tarihsel-kayit' && iv.status !== 'paid' && iv.status !== 'cancelled'
        ? ' <span class="gb-pill gb-pill-sapphire" title="Bu faturanın güncel ödeme durumu muhasebe kaydımızla henüz eşleşmedi. Ödemesini yaptıysanız bize bildirin.">ödeme durumu teyit edilmedi</span>'
        : ''
    }</td>
    <td class="gb-c-dur">${escHtml(iv.due_date || '—')}</td></tr>`).join('');
  // Para birimleri AYRI gösterilir: kur olmadan toplamak yanlış rakam üretir (sunucu da toplamıyor).
  const bakiyeler = f.balances && Object.keys(f.balances).length
    ? Object.entries(f.balances).filter(([, v]) => v !== 0).sort(([a], [b]) => a.localeCompare(b))
    : [[f.currency, f.balance_open] as [string, number]];
  const bakiyeToplam = bakiyeler.reduce((t, [, v]) => t + v, 0);
  const bakiyeMetni = bakiyeler.length
    ? bakiyeler.map(([k, v]) => `${nf(v, 2)} ${escHtml(k)}`).join(' + ')
    : `${nf(0, 2)} ${escHtml(f.currency)}`;
  const balTone = bakiyeToplam > 0 ? 'var(--gem-ruby,#f87171)' : 'var(--gem-emerald,#34d399)';
  const bekleyen = f.pending_confirmation && Object.keys(f.pending_confirmation).length
    ? Object.entries(f.pending_confirmation).filter(([, v]) => v !== 0)
        .sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${nf(v, 2)} ${escHtml(k)}`).join(' + ')
    : '';
  const bekleyenSatiri = bekleyen
    ? `<div class="co-fin-bal"><span class="co-fin-lbl">Kontrol edilen kayıtlar<br><span style="color:var(--muted);font-size:11px">Açık bakiyeye eklenmedi</span></span><span class="co-fin-amt" style="color:var(--muted);font-size:16px">${bekleyen}</span></div>`
    : '';
  // Açık bakiyenin ne kadarı muhasebe kaydıyla eşleşmemiş? Rakamı GİZLEMEDEN neyin teyitli
  // olmadığını söyler; "doğrulanmış borç" iddiası taşımaz (sunucu da taşımıyor).
  const teyitsiz = f.balances_unconfirmed && Object.keys(f.balances_unconfirmed).length
    ? Object.entries(f.balances_unconfirmed).filter(([, v]) => v !== 0)
        .sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${nf(v, 2)} ${escHtml(k)}`).join(' + ')
    : '';
  // Bu metin MÜŞTERİYE hitap eder ("ödediyseniz bize bildirin"). Personel önizlemesinde
  // gösterilmesi anlamsızdı: personel kendine bildiremez ve orada zaten HİÇBİR kayıt
  // muhasebeyle eşleşmiş olamaz (kayıt sistemi okunmuyor). Önizlemede kendi dili kullanılır.
  const teyitsizNotu = (teyitsiz && !f.finance_preview_only)
    ? `<div class="gb-mod-note" style="margin-top:8px">Açık bakiyenin ${teyitsiz} kadarlık kısmının güncel ödeme durumu muhasebe kaydımızla henüz eşleşmedi. Bu tutarı ödediyseniz lütfen bize bildirin; kaydı düzeltiriz.</div>`
    : '';
  // Ölçüm eksikse "borç yok" izlenimi verilmez; iç ayrıntı YAZILMAZ (müşteriye dönük yüzey).
  // Önizleme ile gerçek arıza aynı şey değildir: personele önizlemenin sınırı yazılır, müşteriye
  // dönük metin ise iç ayrıntı taşımaz ("ekibimiz kontrol ediyor").
  const eksikNotu = f.finance_preview_only
    ? '<div class="gb-mod-note" style="margin-top:8px">Personel önizlemesi: muhasebe kaydı bu görünümde okunmaz — tutarlar yalnız dosya kayıtlarından. Güncel ödeme durumu için müşteri panelini kullanın.</div>'
    : f.finance_incomplete
      ? '<div class="gb-mod-note" style="margin-top:8px">Bazı fatura kayıtları şu anda görüntülenemiyor; liste eksik olabilir. Ekibimiz kontrol ediyor.</div>'
      : '';
  /**
   * ANA GÖSTERGE. Personel önizlemesinde (`?tenant=`) muhasebe kaydı OKUNMAZ, bu yüzden orada
   * "Açık bakiye" diye bir GÜNCEL BORÇ iddiası kurulamaz.
   *
   * Ölçüm (29.09.2026, gerçek tarayıcı): aynı müşteri için müşteri görünümü 5.000,00 TRY,
   * personel önizlemesi 125.000,00 TRY gösteriyordu — aradaki 120.000 TRY kayıt sisteminde
   * ÖDENMİŞ bir faturaydı. Uyarı notu eklenmesi bu iddiayı kaldırmadı: başlık hâlâ "Açık bakiye",
   * renk hâlâ borç kırmızısıydı. Artık önizlemede ana satır tutar YERİNE "doğrulanamıyor" der;
   * dosya kayıtlarındaki toplam AYRI ADLA ve nötr renkle, "güncel borç değildir" açıklamasıyla
   * verilir. Bilinmeyen SIFIR BORCA da çevrilmez — tutar gizlenmez, iddiası kaldırılır.
   */
  const bakiyeBlogu = f.finance_preview_only
    ? `<div class="co-fin-bal"><span class="co-fin-lbl">Güncel bakiye</span><span class="co-fin-amt" style="color:var(--muted);font-size:18px">doğrulanamıyor</span></div>
    <div class="co-fin-bal"><span class="co-fin-lbl">Dosya kayıtlarındaki toplam<br><span style="color:var(--muted);font-size:11px">Güncel tahsil edilebilir borç DEĞİLDİR — ödenmiş faturalar da içinde olabilir</span></span><span class="co-fin-amt" style="color:var(--muted);font-size:16px">${bakiyeMetni}</span></div>`
    : `<div class="co-fin-bal"><span class="co-fin-lbl">Açık bakiye</span><span class="co-fin-amt" style="color:${balTone}">${bakiyeMetni}</span></div>`;
  const retNeg = !!(f.retainer && ['görüşülüyor', 'gorusuluyor', 'negotiating'].includes((f.retainer.status || '').toLowerCase()));
  const retVal = retNeg ? 'görüşülüyor' : (f.retainer && f.retainer.amount ? `${nf(f.retainer.amount, 0)} ${escHtml(f.retainer.currency)}/ay` : '');
  const ret = (f.retainer && (retNeg || f.retainer.amount)) ? `<div class="co-fin-bal"><span class="co-fin-lbl">Aylık abonelik (retainer)${f.retainer.services ? ` · <span style="color:var(--muted);font-size:11px">${escHtml(f.retainer.services)}</span>` : ''}</span><span class="co-fin-amt" style="color:var(--gem-sapphire,#60a5fa);font-size:18px">${retVal}</span></div>` : '';
  // Piyasa karşılığı — yalnız sunucu `finance.benchmark` gönderdiyse çizilir.
  // Sunucu kapıları: flag açık + iki tutar da >0 + benchmark > ödenen.
  // Burada EK KONTROL YOK; tek doğruluk sunucuda, aksi hâlde iki yerde iki
  // farklı kural oluşur ve biri sessizce bozulur.
  const bm = f.benchmark;
  const benchmark = bm ? `<div class="co-fin-bal" style="border-top:1px dashed var(--line,#e5e7eb);margin-top:8px;padding-top:10px">
      <span class="co-fin-lbl">Piyasa karşılığı<br><span style="color:var(--muted);font-size:11px">${escHtml(bm.note)}</span></span>
      <span class="co-fin-amt" style="color:var(--muted);font-size:16px">${nf(bm.amount, 0)} ${escHtml(bm.currency)}/ay</span></div>
    <div class="co-fin-bal"><span class="co-fin-lbl">Farkınız</span><span class="co-fin-amt" style="color:var(--gem-emerald,#34d399);font-size:18px">${nf(bm.savings, 0)} ${escHtml(bm.currency)}/ay</span></div>` : '';
  // benchmark-only modunda bakiye ve fatura listesi hiç çizilmez (sunucu zaten
  // göndermiyor). "Fatura yok" yazmak da yanlış olur — fatura var, gösterilmiyor.
  if (f.finance_display === 'benchmark-only') {
    return `<div class="gb-card"><div class="gb-card-title">Hizmet Değeriniz</div>
      ${ret}${benchmark}
      <div class="gb-mod-note" style="margin-top:8px">Fatura ve cari hesap bilgileriniz için hesap yöneticinizle iletişime geçebilirsiniz.</div></div>`;
  }
  return `<div class="gb-card"><div class="gb-card-title">Finans / Cari <span class="gb-card-sub">${f.count} fatura</span></div>
    ${ret}${benchmark}
    ${bakiyeBlogu}
    ${bekleyenSatiri}${teyitsizNotu}${eksikNotu}
    ${f.count ? `<table class="gb-table"><thead><tr><th>Fatura</th><th>Tutar</th><th>Durum</th><th>Vade</th></tr></thead><tbody>${rows}</tbody></table>` : '<div class="gb-empty-sm">Fatura yok</div>'}
    <div class="gb-mod-note" style="margin-top:8px">Salt-okunur — ödeme/işlem Castintech tarafında yürütülür.</div></div>`;
}

// para birimi (tr-TR, ₺ önek)
function try0(n: number | undefined): string { return '₺' + nf(n, 0); }
function try2(n: number | undefined): string { return '₺' + nf(n, 2); }
function pct(n: number | undefined, dec = 1): string { return typeof n === 'number' ? (n * 100).toFixed(dec) + '%' : '—'; }
// ── Faz B: dönem-karşılaştırma yardımcıları — GERÇEK ▲%▼% (rapor campaigns_prev'ten) ──
function deltaPct(cur: number | undefined, prev: number | undefined): number | null {
  const c = typeof cur === 'number' ? cur : 0;
  const p = typeof prev === 'number' ? prev : 0;
  if (p === 0) return c === 0 ? 0 : null; // önceki 0, cari var → oran tanımsız (yeni)
  return (c - p) / p;
}
// goodUp: true→artış iyi(yeşil) / false→artış kötü(kırmızı) / undefined→nötr(gri, ör. harcama)
function deltaBadge(cur: number | undefined, prev: number | undefined, goodUp?: boolean): string {
  const d = deltaPct(cur, prev);
  if (d === null) return `<span class="co-delta co-delta-new" title="önceki dönem 0">yeni</span>`;
  if (Math.abs(d) < 0.0005) return `<span class="co-delta co-delta-flat">— %0</span>`;
  const up = d > 0;
  const tone = goodUp === undefined ? 'flat' : (up === goodUp ? 'good' : 'bad');
  return `<span class="co-delta co-delta-${tone}">${up ? '▲' : '▼'} ${up ? '+' : ''}${(d * 100).toFixed(1)}%</span>`;
}
// mini karşılaştırma çubuğu (inline-SVG, CSP-safe): önceki(gri, üst) vs cari(renkli, alt)
function miniCompareBar(prev: number | undefined, cur: number | undefined, color: string): string {
  const p = Math.max(0, typeof prev === 'number' ? prev : 0);
  const c = Math.max(0, typeof cur === 'number' ? cur : 0);
  const max = Math.max(p, c, 1);
  const wp = Math.max(1, Math.round((p / max) * 100));
  const wc = Math.max(1, Math.round((c / max) * 100));
  return `<svg class="co-cbar" viewBox="0 0 100 20" preserveAspectRatio="none" role="img" aria-hidden="true">
    <rect x="0" y="1" width="${wp}" height="7" rx="2" fill="var(--border)"></rect>
    <rect x="0" y="11" width="${wc}" height="7" rx="2" fill="${color}"></rect>
  </svg>`;
}
// dönem-karşılaştırmalı KPI kartı (delta rozeti + karşılaştırma çubuğu; prev yoksa sade)
function kpiTileB(icon: string, label: string, value: string, color: string, cur: number | undefined, prev: number | undefined, goodUp: boolean | undefined, hasPrev: boolean): string {
  const badge = hasPrev ? deltaBadge(cur, prev, goodUp) : '';
  const bar = hasPrev ? miniCompareBar(prev, cur, color) : '';
  return `<div class="gb-tile co-tile-b" style="--gb-accent:${color}">
    <div class="gb-tile-icon">${icon}</div>
    <div class="gb-tile-body"><div class="gb-tile-value">${escHtml(value)} ${badge}</div><div class="gb-tile-label">${escHtml(label)}</div>${bar}</div>
  </div>`;
}
function sparkPath(vals: number[], w: number, h: number): { line: string; area: string } {
  const n = vals.length;
  if (n < 2) return { line: '', area: '' };
  const max = Math.max(...vals, 1); const min = Math.min(...vals, 0);
  const rng = (max - min) || 1;
  const pts = vals.map((v, i) => { const x = (i / (n - 1)) * w; const y = h - ((v - min) / rng) * h; return `${x.toFixed(1)},${y.toFixed(1)}`; });
  return { line: `M${pts.join(' L')}`, area: `M0,${h} L${pts.join(' L')} L${w},${h} Z` };
}
function trendCard(title: string, series: AdsSeriesPoint[], key: 'spend' | 'clicks' | 'conversions', color: string, fmt: (n: number) => string): string {
  const vals = series.map((p) => Number(p[key]) || 0);
  const w = 280, h = 60;
  const { line, area } = sparkPath(vals, w, h);
  const total = vals.reduce((a, b) => a + b, 0);
  return `<div class="co-trend">
    <div class="co-trend-head"><span class="co-trend-lbl">${escHtml(title)}</span><span class="co-trend-val">${escHtml(fmt(total))}</span></div>
    <svg class="co-trend-svg" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img" aria-label="${escAttr(title)} günlük trend">
      <path d="${area}" fill="${color}" opacity="0.13"></path>
      <path d="${line}" fill="none" stroke="${color}" stroke-width="2" vector-effect="non-scaling-stroke"></path>
    </svg>
  </div>`;
}
function adsTrendCharts(series: AdsSeriesPoint[] | undefined): string {
  if (!series || series.length < 2) return '';
  const start = series[0].day, end = series[series.length - 1].day;
  const _daysSince = Math.floor((Date.now() - Date.parse(end + 'T00:00:00Z')) / 86400000);
  const _fresh = (Number.isFinite(_daysSince) && _daysSince <= 30) ? 'canlı' : 'geçmiş dönem';
  return `<div class="co-trend-wrap">
    <div class="co-trend-cap">📈 Günlük trend <span class="gb-card-sub">${escHtml(start)} → ${escHtml(end)} · ${series.length} gün · ${_fresh}</span></div>
    <div class="co-trend-grid">
      ${trendCard('Harcama', series, 'spend', '#f59e0b', (n) => try0(n))}
      ${trendCard('Tıklama', series, 'clicks', '#3b82f6', (n) => nf(n))}
    </div>
  </div>`;
}
function adsChannelBlock(name: string, ch: AdsChannel): string {
  const t = ch.totals || { spend: 0, clicks: 0, impressions: 0, conversions: 0 };
  const pt = ch.prevTotals || null;
  const hasPrev = !!pt;
  // kanal-düzeyi ortalama CTR (toplam tıklama/gösterim) — dönem karşılaştırmalı
  const curCtr = t.impressions ? t.clicks / t.impressions : undefined;
  const prevCtr = pt && pt.impressions ? pt.clicks / pt.impressions : undefined;
  const camps = (ch.campaigns || []).map(c => {
    const p = c.prev;
    const dSpend = p ? ` ${deltaBadge(c.spend, p.spend, undefined)}` : '';
    const dCtr = p ? ` ${deltaBadge(c.ctr, p.ctr, true)}` : '';
    return `<tr>
    <td class="gb-c-name">${escHtml(c.name || '—')}</td>
    <td>${try0(c.spend)}${dSpend}</td><td>${nf(c.clicks)}</td><td>${nf(c.impressions)}</td>
    <td>${pct(c.ctr, 2)}${dCtr}</td><td>${typeof c.avg_cpc === 'number' ? try2(c.avg_cpc) : '—'}</td>
    <td>${typeof c.impression_share === 'number' ? pct(c.impression_share, 0) : '—'}</td></tr>`;
  }).join('');
  const per = ch.period ? `${ch.period.start} → ${ch.period.end}` : (ch.audit_date || '');
  const prevPer = ch.prevPeriod ? `${ch.prevPeriod.start} → ${ch.prevPeriod.end}` : '';
  const perLine = prevPer
    ? `<span class="gb-card-sub">Cari: ${escHtml(per)} · Önceki: ${escHtml(prevPer)}</span>`
    : `<span class="gb-card-sub">${escHtml(per)}</span>`;
  // Dönüşüm metriği portalda geçici gizli (ölçüm hijyeni); not da kaldırıldı.
  const convNote = '';
  const hasSeries = !!(ch.series && ch.series.length > 1);
  const gran = hasSeries
    ? `<div class="co-ads-note">📊 Aylık KPI + günlük canlı trend (reklam platformu API'si). ▲▼ önceki döneme göre gerçek değişim.</div>`
    : hasPrev
    ? `<div class="co-ads-note">📊 Değerler aylık rapor dönemleri; ▲▼ önceki döneme göre gerçek değişim. Günlük seri henüz akmıyor.</div>`
    : `<div class="co-ads-note">📊 Tek dönem verisi — karşılaştırma sonraki rapor döneminde açılır.</div>`;
  return `<div class="co-ads-ch"><div class="co-ads-head">${escHtml(name)} ${perLine}</div>
    <div class="gb-kpi-grid gb-kpi-row2">
      ${kpiTileB('💸', 'Harcama', try0(t.spend), 'var(--gem-amber,#fbbf24)', t.spend, pt?.spend, undefined, hasPrev)}
      ${kpiTileB('🖱️', 'Tıklama', nf(t.clicks), 'var(--gem-sapphire,#60a5fa)', t.clicks, pt?.clicks, true, hasPrev)}
      ${kpiTileB('👁️', 'Gösterim', nf(t.impressions), 'var(--gem-sapphire,#60a5fa)', t.impressions, pt?.impressions, true, hasPrev)}
      ${kpiTileB('🎯', 'CTR', pct(curCtr, 2), 'var(--gem-emerald,#34d399)', curCtr, prevCtr, true, hasPrev)}
    </div>${adsTrendCharts(ch.series)}${gran}${convNote}
    ${camps ? `<div class="co-ads-tblwrap"><table class="gb-table"><thead><tr><th>Kampanya</th><th>Harcama</th><th>Tıklama</th><th>Gösterim</th><th>CTR</th><th>TBM</th><th>Gös.Payı</th></tr></thead><tbody>${camps}</tbody></table></div>` : ''}</div>`;
}
// ── Müşteri yolculuğu (huni) — GERÇEK lifecycle status'tan, sahte ilerleme YOK ──
const FUNNEL: string[] = ['prospect', 'qualified', 'proposed', 'contracted', 'onboarding', 'active'];
const FUNNEL_LABEL: Record<string, string> = { prospect: 'İlk Temas', qualified: 'Değerlendirme', proposed: 'Teklif', contracted: 'Sözleşme', onboarding: 'Kurulum', active: 'Aktif Hizmet', paused: 'Duraklatıldı', churning: 'Risk', terminated: 'Kapandı',
  // K6 — dondurulmuş teklif. Etiketsiz kalırsa panelde ham 'frozen' yazardı
  // (müşteriye dönük yüzde yarı-İngilizce metin).
  frozen: 'Teklif kapandı' };
const FUNNEL_NEXT: Record<string, string> = { prospect: 'Ekibimiz ihtiyacınızı değerlendiriyor.', qualified: 'Size özel teklif hazırlanıyor.', proposed: 'Teklifinizi değerlendirmenizi bekliyoruz.', contracted: 'Kurulum başlıyor.', onboarding: 'Hesabınız kuruluyor, kısa sürede aktif olacak.', active: 'Hizmetiniz aktif — hesap ekibiniz işlerinizi yürütüyor.' };
function journeyStrip(status: string): string {
  const st = (status || '').toLowerCase();
  const cur = FUNNEL.indexOf(st);
  const steps = FUNNEL.map((k, i) => {
    const cls = cur < 0 ? 'co-step' : i < cur ? 'co-step done' : i === cur ? 'co-step cur' : 'co-step';
    return `<div class="${cls}" title="${escAttr(FUNNEL_LABEL[k] || k)}"><span class="co-step-dot"></span><span class="co-step-lbl">${escHtml(FUNNEL_LABEL[k] || k)}</span></div>`;
  }).join('<span class="co-step-line"></span>');
  const note = cur >= 0 ? (FUNNEL_NEXT[st] || '') : (FUNNEL_LABEL[st] ? `Durum: ${FUNNEL_LABEL[st]}` : 'Durum bilgisi hazırlanıyor.');
  return `<div class="gb-card"><div class="gb-card-title">Yolculuğunuz</div><div class="co-steps">${steps}</div>${note ? `<div class="gb-card-sub" style="margin-top:8px">${escHtml(note)}</div>` : ''}</div>`;
}

// ── Destek talepleri — mevcut talepler + yeni talep (doğrudan hesap ekibinize/GM düşer) ──
// DİKKAT: kategori anahtarları sunucu allowlist'iyle BİREBİR aynı olmalı
// (support | bug | feature_request | question) — aksi halde POST 400 invalid_category.
const SUP_CAT: Record<string, string> = { question: 'Soru', support: 'Destek', feature_request: 'Değişiklik / yeni istek', bug: 'Sorun bildirimi' };
const SUP_STATUS: Record<string, string> = { open: 'Açık', closed: 'Kapandı' };
// Destek formu taslak-koruması: yazılanlar sessionStorage'da tutulur → re-render/reload'da
// KAYBOLMAZ (typing-wipe kalıcı çözümü). Anahtar tenant-scope'lu (çapraz sızıntı yok).
// Taslak anahtarı kiracıya bağlıdır (çapraz sızıntı yok). `?tenant=` müşteri oturumunda boştur,
// o yüzden kiracı YÜKLENEN görünümden alınır; ikisi de yoksa anahtar hiç kullanılmaz.
function coDraftKey(): string {
  const slug = currentTenant() || (vs.kind === 'ready' ? (vs.data.tenant?.slug || '') : '');
  return slug ? `co-sup-draft:${slug}` : '';
}
interface CoDraft { subject: string; cat: string; body: string; }
function coDraftGet(): CoDraft {
  // Anahtar boşsa (kiracı henüz bilinmiyor) taslak OKUNMAZ — başka kiracının taslağı gösterilmez.
  try { const a = coDraftKey(); const raw = a ? sessionStorage.getItem(a) : null; if (raw) { const d = JSON.parse(raw); return { subject: d.subject || '', cat: d.cat || 'question', body: d.body || '' }; } } catch { /* yok say */ }
  return { subject: '', cat: 'question', body: '' };
}
function coDraftClear(): void { try { const a = coDraftKey(); if (a) sessionStorage.removeItem(a); } catch { /* yok say */ } }
(window as unknown as { __coDraftSave?: () => void }).__coDraftSave = () => {
  try {
    const subject = (document.getElementById('co-sup-subject') as HTMLInputElement | null)?.value || '';
    const cat = (document.getElementById('co-sup-cat') as HTMLSelectElement | null)?.value || 'question';
    const body = (document.getElementById('co-sup-body') as HTMLTextAreaElement | null)?.value || '';
    const a = coDraftKey();
    if (a && (subject || body)) sessionStorage.setItem(a, JSON.stringify({ subject, cat, body }));
    else coDraftClear();
  } catch { /* yok say */ }
};
// ── WP3 dilim 2 — kanal mesajları (salt okuma; render client-os-mesajlar.ts'te) ──
let coMsgThread: { konusma: PortalKonusmaBaslik; mesajlar: PortalMesaj[] } | null = null;
let coMsgHata = '';
async function coOpenMsg(id: string): Promise<void> {
  coMsgHata = '';
  try {
    const r = await apiFetch(`/castintech-api/client-os/inbox/conversations/${encodeURIComponent(id)}/messages?${tenantSorgusu()}`);
    const j = await r.json().catch(() => ({ ok: false })) as { ok?: boolean; konusma?: PortalKonusmaBaslik; mesajlar?: PortalMesaj[] };
    if (r.ok && j.ok && j.konusma) coMsgThread = { konusma: j.konusma, mesajlar: Array.isArray(j.mesajlar) ? j.mesajlar : [] };
    else coMsgHata = 'Konuşma açılamadı.';
  } catch { coMsgHata = 'Bağlantı hatası.'; } finally { renderClientOs(); }
}
(window as unknown as { __coOpenMsg?: (id: string) => void }).__coOpenMsg = (id) => { if (id) void coOpenMsg(String(id)); };
(window as unknown as { __coBackMsg?: () => void }).__coBackMsg = () => { coMsgThread = null; coMsgHata = ''; renderClientOs(); };
// ── Destek thread (müşteri talebi tıklayınca ekibin yanıtlarını görür + yanıtlayabilir) ──
interface CoSupMsg { author_kind: string; body: string; created_at: string }
let coSupThread: { id: string; subject: string; status: string; messages: CoSupMsg[] } | null = null;
let coSupBusy = false;
async function coOpenSupport(id: string): Promise<void> {
  coSupBusy = true; renderClientOs();
  try {
    const r = await apiFetch(`/castintech-api/client-os/support/requests/${encodeURIComponent(id)}`);
    const j = await r.json().catch(() => ({ ok: false })) as { ok?: boolean; request?: { id: string; subject: string; customer_status: string }; messages?: CoSupMsg[] };
    if (r.ok && j.ok && j.request) coSupThread = { id: j.request.id, subject: j.request.subject, status: j.request.customer_status, messages: Array.isArray(j.messages) ? j.messages : [] };
  } catch { /* yok say */ } finally { coSupBusy = false; renderClientOs(); }
}
(window as unknown as { __coOpenSup?: (id: string) => void; __coBackSup?: () => void; __coSupReply?: () => void }).__coOpenSup = (id) => { void coOpenSupport(id); };
(window as unknown as { __coBackSup?: () => void }).__coBackSup = () => { coSupThread = null; renderClientOs(); };
(window as unknown as { __coSupReply?: () => void }).__coSupReply = () => { void (async () => {
  if (coSupBusy || !coSupThread) return;
  const ta = document.getElementById('co-sup-reply') as HTMLTextAreaElement | null;
  const body = (ta?.value || '').trim(); const msg = document.getElementById('co-sup-reply-msg');
  if (!body) { if (msg) msg.textContent = 'Mesaj boş.'; return; }
  coSupBusy = true; if (msg) msg.textContent = 'Gönderiliyor…';
  try {
    const r = await apiFetch(`/castintech-api/client-os/support/requests/${encodeURIComponent(coSupThread.id)}/messages`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }) });
    const j = await r.json().catch(() => ({ ok: false })) as { ok?: boolean };
    if (r.ok && j.ok) { await coOpenSupport(coSupThread.id); } else if (msg) { msg.textContent = 'Hata.'; }
  } catch { if (msg) msg.textContent = 'Bağlantı hatası.'; } finally { coSupBusy = false; }
})(); };
function supportThreadHtml(): string {
  const th = coSupThread!;
  const msgs = th.messages.length ? th.messages.map((m) => {
    const mine = m.author_kind === 'customer';
    return `<div style="padding:9px 11px;border-radius:8px;margin-bottom:8px;background:${mine ? 'var(--card2)' : 'var(--co-acc-soft, rgba(47,67,166,.08))'}">
      <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--muted);margin-bottom:3px"><span>${mine ? 'Siz' : 'Ekibiniz'}</span><span>${escHtml(m.created_at.slice(0, 16).replace('T', ' '))}</span></div>
      <div style="font-size:14px;white-space:pre-wrap;line-height:1.5">${escHtml(m.body)}</div></div>`;
  }).join('') : '<div class="gb-empty-sm">Henüz mesaj yok.</div>';
  const closed = th.status === 'closed';
  const reply = closed ? '<div class="gb-card-sub" style="margin-top:8px">Bu talep kapatıldı.</div>'
    : `<textarea id="co-sup-reply" class="gb-input" rows="2" maxlength="4000" placeholder="Yanıt yazın…"></textarea>
       <div style="margin-top:8px"><button class="gb-meeting-btn" onclick="window.__coSupReply&&window.__coSupReply()">Gönder</button> <span id="co-sup-reply-msg" class="gb-card-sub"></span></div>`;
  return `<div class="gb-card"><div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;margin-bottom:8px">
      <button class="gb-meeting-btn" onclick="window.__coBackSup&&window.__coBackSup()">← Talepler</button>
      <span class="gb-pill gb-pill-${closed ? 'muted' : 'sapphire'}">${escHtml(SUP_STATUS[th.status] || th.status)}</span></div>
    <div class="gb-card-title">${escHtml(th.subject)}</div>
    <div style="margin:10px 0">${msgs}</div>${reply}</div>`;
}
function supportCard(items: SupportItem[] | null | undefined): string {
  if (coSupThread) return supportThreadHtml();
  const list = Array.isArray(items) ? items : [];
  const rows = list.slice(0, 20).map((r) => `<tr style="cursor:pointer" onclick="window.__coOpenSup&&window.__coOpenSup('${r.id}')"><td>${escHtml(r.subject)}</td><td>${escHtml(SUP_CAT[r.category] || r.category)}</td><td><span class="gb-pill gb-pill-${r.customer_status === 'closed' ? 'muted' : 'sapphire'}">${escHtml(SUP_STATUS[r.customer_status] || r.customer_status)}</span></td></tr>`).join('');
  const table = list.length
    ? `<table class="gb-table"><thead><tr><th>Konu</th><th>Kategori</th><th>Durum</th></tr></thead><tbody>${rows}</tbody></table><div class="gb-card-sub" style="margin-top:6px">Yanıtları görmek için bir talebe tıklayın.</div>`
    : `<div class="gb-empty-sm">Henüz destek talebiniz yok. Aşağıdan yeni talep açabilirsiniz — talebiniz doğrudan hesap ekibinize düşer.</div>`;
  const d = coDraftGet();
  const opt = (v: string, l: string) => `<option value="${v}"${d.cat === v ? ' selected' : ''}>${l}</option>`;
  const form = `<div class="co-support-form" style="margin-top:12px;display:flex;flex-direction:column;gap:8px">
    <input id="co-sup-subject" class="gb-input" placeholder="Konu" maxlength="180" value="${escAttr(d.subject)}" oninput="window.__coDraftSave&&window.__coDraftSave()" />
    <select id="co-sup-cat" class="gb-input" onchange="window.__coDraftSave&&window.__coDraftSave()">${opt('question', 'Soru')}${opt('support', 'Destek')}${opt('feature_request', 'Değişiklik / yeni istek')}${opt('bug', 'Sorun bildirimi')}</select>
    <textarea id="co-sup-body" class="gb-input" placeholder="Kısaca açıklayın…" maxlength="4000" rows="2" oninput="window.__coDraftSave&&window.__coDraftSave()">${escHtml(d.body)}</textarea>
    <div><button class="gb-meeting-btn" onclick="window.__coSupportSubmit&&window.__coSupportSubmit()">Talep aç</button> <span id="co-sup-msg" class="gb-card-sub"></span></div>
  </div>`;
  return `<div class="gb-card"><div class="gb-card-title">💬 Ajanınızla İletişim <span class="gb-card-sub">${list.length} konu</span></div>
    <div class="gb-card-sub" style="margin:-4px 0 10px">Soru sorabilir, destek isteyebilir veya değişiklik talep edebilirsiniz. Mesajınız doğrudan hesap ekibinize ulaşır; yanıtlar bu sayfada aynı konu altında görünür.</div>
    ${table}${form}</div>`;
}
(window as unknown as { __coSupportSubmit?: () => Promise<void> }).__coSupportSubmit = async () => {
  const subj = ((document.getElementById('co-sup-subject') as HTMLInputElement | null)?.value || '').trim();
  const cat = (document.getElementById('co-sup-cat') as HTMLSelectElement | null)?.value || 'question';
  const body = ((document.getElementById('co-sup-body') as HTMLTextAreaElement | null)?.value || '').trim();
  const msg = document.getElementById('co-sup-msg');
  if (!subj) { if (msg) msg.textContent = 'Konu gerekli.'; return; }
  if (msg) msg.textContent = 'Gönderiliyor…';
  try {
    const r = await apiFetch('/castintech-api/client-os/support/requests', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ subject: subj, category: cat, body }) });
    const j = await r.json().catch(() => ({ ok: false }));
    if (r.ok && j.ok) { coDraftClear(); if (msg) msg.textContent = 'Talebiniz alındı ✓'; void loadClientOs().then(renderClientOs); }
    else { if (msg) msg.textContent = 'Hata: ' + (j.error || ('HTTP ' + r.status)); }
  } catch { if (msg) msg.textContent = 'Bağlantı hatası.'; }
};

// Uzun şirket unvanını kısalt (müşteri-dostu başlık).
function shortName(n: string): string {
  const s = (n || '').replace(/\s+(Sanayi\s+ve\s+Ticaret|San\.?\s*ve\s*Tic\.?|Limited\s+Şirketi|Ltd\.?\s*Şti\.?|Anonim\s+Şirketi|A\.?\s*Ş\.?|Ticaret\s+Limited\s+Şirketi)\b.*$/i, '').trim();
  return s || n;
}
// ── Hizmet-merkezli (anlaşma kapsamı) ── her abone-hizmet bir bölüm.
const SERVICE_MATCH: Record<string, RegExp> = {
  'google-ads': /google|ads|reklam|arama/i, 'ads-management': /ads|reklam|google|meta/i, 'meta-ads': /meta|instagram|facebook|sosyal/i,
  'web-management': /web|site|seo/i, 'seo': /seo|arama|görünür/i, 'sosyal-medya': /sosyal|instagram|içerik/i,
  'castdesign': /tasarım|web|prototip|revizyon/i, 'icerik': /içerik|video|görsel|metin/i, 'lead-generation': /lead|funnel/i,
  'web-sitesi': /web|site/i, 'aeo': /aeo|cevap|faq|sss/i, 'aio-geo': /aio|geo|llms|yapay/i, 'analitik': /analitik|analytics|ölçüm|tag|ga4/i,
};
function serviceProjects(sub: string, projects: Item[]): Item[] {
  const rx = SERVICE_MATCH[sub]; if (!rx) return [];
  return projects.filter(p => rx.test(p.ptype || '') || rx.test(p.slug || '') || rx.test(p.title || ''));
}
// Bilinmeyen (yeni alınan) hizmet slug'ı → okunur ada çevir → hemen sisteme adapte.
function prettifySlug(s: string): string {
  return (s || '').split(/[-_]/).filter(Boolean).map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1)).join(' ') || s;
}
function serviceSection(sub: string, d: Overview, used: Set<string>): string {
  // MODULES'ta yoksa generic kart üret — yeni anlaşma/iş eklenince portal OTOMATİK gösterir.
  const m = MODULES[sub] || { icon: '📦', label: prettifySlug(sub), note: 'Hizmet' };
  const projs = serviceProjects(sub, d.projects); projs.forEach(p => used.add(p.slug));
  let perf = '';
  if ((sub === 'google-ads' || sub === 'ads-management') && d.ads?.google) perf += adsChannelBlock('🔎 Google Ads', d.ads.google);
  if ((sub === 'google-ads' || sub === 'ads-management') && d.ads?.google_secondary) perf += adsChannelBlock('🔎 Google Ads — ' + escHtml(d.ads.google_secondary.label || '2. Hesap'), d.ads.google_secondary);
  if ((sub === 'meta-ads' || sub === 'ads-management' || sub === 'sosyal-medya') && d.ads?.meta) perf += adsChannelBlock('📣 Meta Ads' + (d.ads.meta.label ? ' — ' + escHtml(d.ads.meta.label) : ''), d.ads.meta);
  const st0 = projs[0]?.state ? (projs[0].state || '').toLowerCase() : '';
  const _lc = (d.tenant?.status || '').toLowerCase();
  const _lcActive = !_lc || _lc === 'active';
  const statusPill = st0
    ? `<span class="gb-pill gb-pill-sapphire">${escHtml(STATE_LABEL[st0] || st0)}</span>`
    : (_lcActive
      ? `<span class="gb-pill gb-pill-emerald">Aktif</span>`
      : `<span class="gb-pill gb-pill-muted">${escHtml(FUNNEL_LABEL[_lc] || 'Pasif')}</span>`);
  const body = projs.length
    ? projs.map(projectTracker).join('')
    : '';
  // "Ekibinizin Yaptıkları" (done) + "Sıradaki Adımlar" (planned) — vault raporlarından (redakteli).
  const acts = (d.activity || []).filter((a) => a.service === sub);
  const actBlock = (title: string, icon: string, items: ServiceActivity[]) => items.length
    ? `<div class="co-act"><div class="co-act-title">${icon} ${title}</div>${items.slice(0, 6).map((a) => `<div class="co-act-item"><div class="co-act-h">${escHtml(a.title)}${a.date ? `<span class="co-act-date">${escHtml(a.date)}</span>` : ''}</div>${a.summary ? `<div class="co-act-sum">${escHtml(a.summary)}</div>` : ''}</div>`).join('')}</div>`
    : '';
  const activityHtml = actBlock('Ekibinizin Yaptıkları', '✅', acts.filter((a) => a.status === 'done')) + actBlock('Sıradaki Adımlar', '📌', acts.filter((a) => a.status === 'planned'));
  return `<div class="gb-card co-service">
    <div class="co-service-head"><div class="co-service-ico">${m.icon}</div>
      <div class="co-service-titles"><div class="co-service-name">${escHtml(m.label)}</div><div class="co-service-note">${escHtml(m.note)}</div></div>
      ${statusPill}</div>
    ${body}${perf}${activityHtml}
  </div>`;
}
// ── Panel 2.0 P1 — açıklamalı hizmet kartları (tenants.metadata.services[]) ──
// Yılmaz 2026-07-24 kırılımı: her hizmet ayrı kart (başlık + kapsam açıklaması +
// ilgili kanal performansı + o hizmete ait işler/aktiviteler). services[] boşsa
// eski subscriptions yolu aynen çalışır (diğer tenant'lar etkilenmez).
const SVC_CH_ICON: Record<string, string> = { web: '🌐', google: '🔎', google_secondary: '🔎', meta: '📣' };
function activityBlocks(acts: ServiceActivity[]): string {
  const blk = (title: string, icon: string, items: ServiceActivity[]) => items.length
    ? `<div class="co-act"><div class="co-act-title">${icon} ${title}</div>${items.slice(0, 6).map((a) => `<div class="co-act-item"><div class="co-act-h">${escHtml(a.title)}${a.date ? `<span class="co-act-date">${escHtml(a.date)}</span>` : ''}</div>${a.summary ? `<div class="co-act-sum">${escHtml(a.summary)}</div>` : ''}</div>`).join('')}</div>`
    : '';
  return blk('Ekibinizin Yaptıkları', '✅', acts.filter((a) => a.status === 'done')) + blk('Sıradaki Adımlar', '📌', acts.filter((a) => a.status === 'planned'));
}
// CA-KAYIT-004 — SADE MÜŞTERİ UYARISI. Yalnız 'unavailable' durumunda çıkar;
// 'ok'/'empty' ve tenant/hizmet yokluğunda ('not_applicable') ÇIKMAZ. Metin
// sabittir — CRM/Twenty/token/SQL/kod/stack/teknik neden GÖSTERİLMEZ. Sayfayı
// doldurmamak için en fazla iki yerde çizilir: hizmet bölümünün başı +
// Yaklaşan Adımlar.
const WORK_RECORDS_NOTICE =
  'İş kayıtlarının bir bölümü şu anda yüklenemiyor. Daha sonra tekrar deneyebilirsiniz.';

export function workRecordsNotice(status: WorkRecordsStatus | null | undefined): string {
  if (status !== 'unavailable') return '';
  return `<div class="gb-card-sub co-wr-note">ⓘ ${escHtml(WORK_RECORDS_NOTICE)}</div>`;
}

function campaignNotesBlock(notes: CampaignNote[] | null | undefined): string {
  const list = Array.isArray(notes) ? notes : [];
  if (!list.length) return '';
  const rows = list.map((n) => `<div class="co-act-item"><div class="co-act-h">🎬 ${escHtml(n.label)}</div><div class="co-act-sum">${escHtml(n.note)}</div></div>`).join('');
  return `<div class="co-act"><div class="co-act-title">📣 Kampanya Kırılımı <span class="gb-card-sub">her kampanyanın hedefi</span></div>${rows}</div>`;
}
// Aktivite → hizmet eşlemesi: kanal tipi + alan-adı işareti (ör. "mervakspro" geçen
// Google işi Pro kartına düşer). Her aktivite tek karta gider (usedIdx).
// Kural `client-os-hizmet-eslesme.ts`'te: BEYAN ÖNCE, tahmin sonra. Buradaki kopya kaldırıldı
// (11.10.2026) — sunucu `service:` beyanını okurken ekran onu metin tahminine geri çeviriyordu ve
// çok hizmetli bir raporu ikinci hizmet kartında hiç göstermiyordu. Ayrı dosya olmasının nedeni
// bu dosyanın `window`a yazması: birim testinde import EDİLEMİYOR, kural ölçüsüz kalıyordu.
function svcActivity(svc: ServiceDef, d: Overview, usedIdx: Set<number>): ServiceActivity[] {
  // Katalog anahtarları `d.services`'ten gelir: beyan BAŞKA bir kartı işaret ediyorsa bu kartın
  // onu metin tahminiyle sahiplenmesi engellenir.
  const tumAnahtarlar = (Array.isArray(d.services) ? d.services : []).map((s) => s && s.key).filter(Boolean) as string[];
  return kartAktiviteleri(svc, d.activity || [], usedIdx, tumAnahtarlar);
}
function serviceCardV2(svc: ServiceDef, d: Overview, used: Set<string>, usedAct: Set<number>): string {
  const chans = svc.channels || [];
  const icon = SVC_CH_ICON[chans[0] || ''] || '📦';
  let perf = '';
  if (chans.includes('google') && d.ads?.google) perf += adsChannelBlock('🔎 Google Ads' + (d.ads.google.label ? ' — ' + escHtml(d.ads.google.label) : ''), d.ads.google);
  if (chans.includes('google_secondary') && d.ads?.google_secondary) perf += adsChannelBlock('🔎 Google Ads — ' + escHtml(d.ads.google_secondary.label || '2. Hesap'), d.ads.google_secondary);
  if (chans.includes('meta') && d.ads?.meta) {
    perf += adsChannelBlock('📣 Meta Ads' + (d.ads.meta.label ? ' — ' + escHtml(d.ads.meta.label) : ''), d.ads.meta);
    perf += campaignNotesBlock(d.campaign_notes);
  }
  // ilgili işler: alan-adı token'ı tam-kelime eşleşmesi (mervaks ≠ mervakspro)
  const tok = (svc.domain || svc.key).split('.')[0];
  const rx = new RegExp(tok.replace(/[.*+?^${}()|[\]\\]/g, '') + '(?![a-z0-9])', 'i');
  const projs = d.projects.filter((p) => !used.has(p.slug) && (rx.test(p.title) || rx.test(p.slug) || rx.test(p.ptype || '')));
  projs.forEach((p) => used.add(p.slug));
  const acts = svcActivity(svc, d, usedAct);
  const statusPill = (svc.status || 'active') === 'active'
    ? `<span class="gb-pill gb-pill-emerald">Aktif</span>`
    : `<span class="gb-pill gb-pill-muted">${escHtml(svc.status || '')}</span>`;
  return `<div class="gb-card co-service">
    <div class="co-service-head"><div class="co-service-ico">${icon}</div>
      <div class="co-service-titles"><div class="co-service-name">${escHtml(svc.title)}</div><div class="co-service-note">${escHtml(svc.description || '')}</div></div>
      ${statusPill}</div>
    ${projs.map(projectTracker).join('')}${perf}${activityBlocks(acts)}
  </div>`;
}

// Yaklaşan Adımlar (todo) — tüm hizmetlerdeki planned aktiviteleri topla ("neyin ne zaman yapılacağı").
interface SatSurvey { id: string; survey_type: string; milestone: string; milestone_label: string; prompt: string; scale: { min: number; max: number; kind: string; low_is_good: boolean }; }
interface Satisfaction { pending: SatSurvey[]; recent?: Array<{ survey_type: string; score: number; responded_at: string }>; }

const coSatSel: Record<string, number> = {};

function satisfactionCard(s: Satisfaction | null | undefined): string {
  if (!s || !Array.isArray(s.pending) || s.pending.length === 0) return '';
  const items = s.pending.map((sv) => {
    const nums: string[] = [];
    for (let i = sv.scale.min; i <= sv.scale.max; i++) {
      nums.push(`<button type="button" class="co-sat-num" data-v="${i}" onclick="window.__coSatPick&&window.__coSatPick('${escAttr(sv.id)}',${i})">${i}</button>`);
    }
    const ends = sv.scale.kind === 'ces'
      ? '<span>Hiç çaba</span><span>Çok çaba</span>'
      : (sv.scale.kind === 'nps' ? '<span>Hiç olası değil</span><span>Kesinlikle</span>' : '<span>Memnun değilim</span><span>Çok memnunum</span>');
    return `<div class="co-sat-item" data-sat="${escAttr(sv.id)}">
      <div class="co-sat-mile">${escHtml(sv.milestone_label)}</div>
      <div class="co-sat-q">${escHtml(sv.prompt)}</div>
      <div class="co-sat-scale">${nums.join('')}</div>
      <div class="co-sat-ends">${ends}</div>
      <textarea id="co-sat-c-${escAttr(sv.id)}" class="gb-input co-sat-comment" rows="2" maxlength="2000" placeholder="Eklemek istedikleriniz (opsiyonel)"></textarea>
      <div class="co-sat-foot"><button class="gb-meeting-btn co-sat-submit" disabled onclick="window.__coSatSubmit&&window.__coSatSubmit('${escAttr(sv.id)}')">Gönder</button> <span class="gb-card-sub" id="co-sat-msg-${escAttr(sv.id)}"></span></div>
    </div>`;
  }).join('');
  return `<style>
    .co-sat-card .co-sat-item{padding:12px 0;border-top:1px solid var(--co-border,#e6e8f0)}
    .co-sat-card .co-sat-item:first-of-type{border-top:none}
    .co-sat-mile{font-size:11px;opacity:.6;margin-bottom:2px}
    .co-sat-q{font-weight:600;margin-bottom:8px}
    .co-sat-scale{display:flex;flex-wrap:wrap;gap:6px}
    .co-sat-num{min-width:38px;height:38px;border-radius:8px;border:1px solid var(--co-border,#d4d8e6);background:var(--co-surface,#fff);color:inherit;cursor:pointer;font-weight:600;transition:all .12s}
    .co-sat-num:hover{border-color:#2f43a6}
    .co-sat-num.co-sat-on{background:#2f43a6;color:#fff;border-color:#2f43a6}
    .co-sat-ends{display:flex;justify-content:space-between;font-size:11px;opacity:.55;margin:4px 2px 8px}
    .co-sat-comment{width:100%;margin-bottom:8px}
    .co-sat-foot{display:flex;align-items:center;gap:10px}
  </style>
  <div class="gb-card co-sat-card">
    <div class="co-section-title">📝 Görüşünüz bizim için değerli</div>
    <div class="gb-card-sub" style="margin-bottom:6px">Kısa bir değerlendirme, hizmetimizi geliştirmemize yardımcı olur.</div>
    ${items}</div>`;
}

// Ölçek seçimi — TAM RENDER YOK (textarea içeriği korunur); yalnız DOM highlight.
(window as unknown as { __coSatPick?: (id: string, val: number) => void }).__coSatPick = (id, val) => {
  coSatSel[id] = val;
  const wrap = document.querySelector(`[data-sat="${CSS.escape(id)}"]`);
  if (!wrap) return;
  wrap.querySelectorAll('.co-sat-num').forEach((b) => b.classList.remove('co-sat-on'));
  const on = wrap.querySelector(`.co-sat-num[data-v="${val}"]`);
  if (on) on.classList.add('co-sat-on');
  const btn = wrap.querySelector('.co-sat-submit') as HTMLButtonElement | null;
  if (btn) btn.disabled = false;
};

(window as unknown as { __coSatSubmit?: (id: string) => Promise<void> }).__coSatSubmit = async (id) => {
  const setMsg = (t: string): void => { const s = document.getElementById('co-sat-msg-' + id); if (s) s.textContent = t; };
  const val = coSatSel[id];
  if (val == null) { setMsg('Lütfen bir puan seçin'); return; }
  const ta = document.getElementById('co-sat-c-' + id) as HTMLTextAreaElement | null;
  const comment = ta ? ta.value : '';
  setMsg('Gönderiliyor…');
  try {
    const r = await apiFetch(`/castintech-api/client-os/satisfaction/${encodeURIComponent(id)}/respond`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ score: val, comment }) });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j && j.ok) { delete coSatSel[id]; setMsg('Teşekkürler! Görüşünüz alındı ✓'); void loadClientOs().then(renderClientOs); }
    else { setMsg('Gönderilemedi, lütfen tekrar deneyin'); }
  } catch { setMsg('Bağlantı hatası'); }
};

function upcomingCard(d: Overview): string {
  const planned = (d.activity || []).filter((a) => a.status === 'planned');
  // Kaynak okunamadıysa kart boş olsa bile eksiklik GÖRÜNÜR olmalı; vault'tan
  // gelen mevcut planlı adımlar korunur, üzerine yalnız bir satır uyarı eklenir.
  const notice = workRecordsNotice(d.work_records_status);
  if (!planned.length) {
    return notice
      ? `<div class="gb-card co-upcoming"><div class="gb-card-title">📌 Yaklaşan Adımlar</div>${notice}</div>`
      : '';
  }
  // Bu liste hizmetten BAĞIMSIZ: sunucu çok hizmetli bir işi her hizmet için bir satır olarak
  // gönderdiği için burada aynı iş iki kez çıkıyor, 8'lik yerin ikisini yiyor ve başlıktaki sayıyı
  // şişiriyordu (ölçüldü 11.10.2026). Satırlar işe indiriliyor, hizmetler tek satırda toplanıyor.
  const isler = benzersizIsler(planned);
  const items = isler.slice(0, 8).map((a) => {
    const svc = a.hizmetler.map((h) => MODULES[h]?.label || h).join(' · ');
    return `<div class="co-todo-item"><span class="co-todo-dot"></span><div class="co-todo-body">
      <div class="co-todo-title">${escHtml(a.title)}${a.date ? `<span class="co-todo-date">${escHtml(a.date)}</span>` : ''}</div>
      <div class="co-todo-svc">${escHtml(svc)}${a.summary ? ' · ' + escHtml(a.summary) : ''}</div></div></div>`;
  }).join('');
  return `<div class="gb-card co-upcoming"><div class="gb-card-title">📌 Yaklaşan Adımlar <span class="gb-card-sub">${isler.length}</span> <span class="co-hint">— ekibinizin sıradaki planı</span></div>${notice}<div class="co-todo-list">${items}</div></div>`;
}
// Finans çerçevesi — Castacc eşlemesi yoksa dürüst boş.
function financeFramework(): string {
  return `<div class="gb-card"><div class="gb-card-title">Finans — Bakiye · Fatura · Hesap Ekstresi</div>
    <div class="gb-empty-sm">Bakiye, fatura durumu ve hesap ekstreniz muhasebe sistemine bağlandığında burada görünecek. O zamana kadar faturalandırma Castintech tarafından yürütülür.</div></div>`;
}

// İş Adımlarınız — müşteri-görünür görevler (client_summary + durum). "Her iş açıklamalı."
function tasksCard(tasks: ClientTask[] | null | undefined): string {
  const list = (Array.isArray(tasks) ? tasks : []).filter((t) => t.summary || t.title);
  if (!list.length) return '';
  const rows = list.slice(0, 30).map((t) => {
    const text = t.summary || t.title;
    const pill = t.done ? 'gb-pill-emerald' : 'gb-pill-sapphire';
    return `<div class="co-task-item">
      <span class="co-task-dot ${t.done ? 'done' : ''}"></span>
      <div class="co-task-body"><div class="co-task-text">${escHtml(text)}</div>${t.title && t.summary ? `<div class="co-task-ref">${escHtml(t.title)}</div>` : ''}</div>
      <span class="gb-pill ${pill}">${escHtml(t.status)}</span>
    </div>`;
  }).join('');
  return `<div class="gb-card"><div class="gb-card-title">🗂️ İş Adımlarınız <span class="gb-card-sub">ekibinizin yürüttüğü işler</span></div><div class="co-task-list">${rows}</div></div>`;
}
// İşlem Geçmişi / Hesap Kaydı — müşteri-görünür hesap olayları (account-record).
// Sürekli yürütülen hizmetler — her ay tekrarlanan rutin işler (tenants.metadata.routine_services).
// Aylık kayıtlarda tekrar edilmez; müşteri kalıcı blokta görür (P1.5 backfill, Yılmaz onaylı metin).
function routineServicesCard(rs: string[] | null | undefined): string {
  if (!rs || !rs.length) return '';
  const items = rs.map((r) => `<li>${escHtml(String(r))}</li>`).join('');
  return `<div class="gb-card"><div class="gb-card-title">🔁 Sürekli Yürütülen Hizmetler <span class="gb-card-sub">her ay düzenli</span></div>
    <ul class="co-routine-list">${items}</ul>
    <div class="gb-mod-note" style="margin-top:6px">Bu çalışmalar aylık kayıtlarda ayrıca listelenmez; hizmetin sürekli parçasıdır.</div></div>`;
}
function accountLogCard(log: AccountLogEntry[] | null | undefined): string {
  const list = Array.isArray(log) ? log : [];
  if (!list.length) return '';
  const rows = list.slice(0, 20).map((e) => {
    const items = (e.items || []).map((it) => `<li>${escHtml(it)}</li>`).join('');
    return `<div class="co-log-item">
      <div class="co-log-head"><span class="co-log-title">${escHtml(e.title)}</span>${e.date ? `<span class="co-log-date">${escHtml(e.date)}</span>` : ''}</div>
      ${items ? `<ul class="co-log-items">${items}</ul>` : (e.summary ? `<div class="co-log-sum">${escHtml(e.summary)}</div>` : '')}
    </div>`;
  }).join('');
  return `<div class="gb-card"><div class="gb-card-title">📋 İşlem Geçmişi <span class="gb-card-sub">hesap kayıtlarınız</span></div>${rows}</div>`;
}
// F2 -- coklu-isletme portfoy secici.
//
// MUSTERI: yalniz ayni ana-gruptaki kardes isletmeler (or. Azgen grubu -> Mervaks + Mervaks Pro).
// Tek markali musteride hic cizilmez. Bu davranis IZOLASYONUN parcasi, degismemeli.
//
// STAFF (2026-08-16): eskiden staff da ayni grup filtresine takiliyordu -> grubu olmayan bir
// musteriye (or. KalDer) bakarken secici HIC cizilmiyordu ve panelden panele gecmek icin
// Ic Panel -> Musteri Portallari -> Ac yolunu izlemek gerekiyordu. Oysa `/businesses` ucu
// staff'a TUM tenant'lari zaten donduruyor (notify/index.ts:7831) — eksik olan yalnizca cizim.
// Staff icin grup filtresi uygulanmaz; tum musteriler gruplanmis bir acilir listede gelir.
// Guard: tests/client-os-staff-switcher.test.js — musteri dali genisletilemez.
function portfolioSwitcher(): string {
  if (coStaffView) return staffTenantPicker();
  const cur = coBusinesses.find(b => b.is_current);
  const grp = cur ? cur.parent_group : null;
  const sibs = grp ? coBusinesses.filter(b => b.parent_group === grp) : coBusinesses.filter(b => b.is_current);
  if (sibs.length < 2) return '';
  const pills = sibs.map(b => b.is_current
    ? `<span class="co-biz-pill on" aria-current="true">${escHtml(shortName(b.name))}</span>`
    : `<button type="button" class="co-biz-pill" onclick="window.__coSwitchBusiness && window.__coSwitchBusiness('${escAttr(b.slug)}')">${escHtml(shortName(b.name))}</button>`).join('');
  return `<div class="co-portfolio"><span class="co-portfolio-lbl">\🏢 ${escHtml(grp || 'Isletmeleriniz')}</span><div class="co-biz-pills">${pills}</div></div>`;
}
// Staff-tekeli musteri secici. Yerli <select> bilincli tercih: klavyeyle yazarak arama,
// ekran okuyucu destegi ve mobil uyum bedava gelir; ozel bir acilir liste bunlarin hepsini
// yeniden yazmayi gerektirirdi. <optgroup> ana-gruplari korur (Azgen -> Mervaks/Mervaks Pro).
function staffTenantPicker(): string {
  if (coBusinesses.length < 2) return '';
  const cur = coBusinesses.find(b => b.is_current);
  const gruplar = new Map<string, Business[]>();
  for (const b of coBusinesses) {
    const k = b.parent_group || '';
    if (!gruplar.has(k)) gruplar.set(k, []);
    gruplar.get(k)!.push(b);
  }
  // Grubu olanlar once (kardesler bir arada dursun), sonra tekil musteriler.
  const sirali = [...gruplar.entries()].sort((a, b) => (a[0] ? 0 : 1) - (b[0] ? 0 : 1) || a[0].localeCompare(b[0], 'tr'));
  const opt = (b: Business): string =>
    `<option value="${escAttr(b.slug)}"${b.is_current ? ' selected' : ''}>` +
    `${escHtml(shortName(b.name))}${b.status && b.status !== 'active' ? ' — ' + escHtml(b.status) : ''}</option>`;
  const govde = sirali.map(([grp, list]) => {
    const items = list.slice().sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(opt).join('');
    return grp ? `<optgroup label="${escAttr(grp)}">${items}</optgroup>` : items;
  }).join('');
  return `<div class="co-portfolio"><span class="co-portfolio-lbl">🪟 Müşteri (${coBusinesses.length})</span>` +
    `<select class="co-biz-select" aria-label="Müşteri paneli seç" ` +
    `onchange="window.__coSwitchBusiness && window.__coSwitchBusiness(this.value)">${govde}</select>` +
    `<span class="gb-card-sub">${escHtml(cur ? shortName(cur.name) : '')}</span></div>`;
}
// İsimden monogram (1-2 harf) — sahte-foto YOK (Direction-A kuralı).
function monogram(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const s = parts.length >= 2 ? (parts[0][0] + parts[1][0]) : (parts[0] || '?').slice(0, 2);
  return s.toUpperCase();
}
function deliveriesCard(list: ClientDelivery[] | null | undefined): string {
  const items = Array.isArray(list) ? list : [];
  if (!items.length) return '';
  const rows = items.slice(0, 20).map((dv) => {
    const statusPill = dv.officiallyAccepted ? 'gb-pill-emerald' : 'gb-pill-sapphire';
    const link = dv.link ? `<a class="co-deliv-link" href="${escAttr(dv.link)}" target="_blank" rel="noopener">Görüntüle ›</a>` : '';
    let action: string;
    if (dv.officiallyAccepted) action = `<span class="gb-pill gb-pill-emerald">✓ Kabul edildi</span>`;
    else if (dv.clientApproved) action = `<span class="co-deliv-signaled">✓ Onayınız iletildi — resmi kabul ekibinizde</span>`;
    else action = `<button class="co-deliv-btn" onclick="window.__coApproveDelivery&&window.__coApproveDelivery('${escAttr(dv.ref)}')">Onayla</button>`;
    return `<div class="co-deliv-item">
      <div class="co-deliv-body">
        <div class="co-deliv-title">${escHtml(dv.title)} <span class="gb-pill ${statusPill}">${escHtml(dv.status)}</span></div>
        ${dv.summary ? `<div class="co-deliv-sum">${escHtml(dv.summary)}</div>` : ''}
        ${link}
      </div>
      <div class="co-deliv-action">${action}</div>
    </div>`;
  }).join('');
  return `<div class="gb-card"><div class="gb-card-title">📦 Teslimatlarınız <span class="gb-card-sub">rapor · video · görsel</span></div><div class="co-deliv-list">${rows}</div></div>`;
}
function purchasesCard(list: ClientPurchase[] | null | undefined): string {
  const items = Array.isArray(list) ? list : [];
  if (!items.length) return '';
  const rows = items.slice(0, 30).map((p) => {
    const cls = p.paymentRaw === 'paid' ? 'co-pay-paid'
      : p.paymentRaw === 'overdue' ? 'co-pay-overdue'
      : (p.paymentRaw === 'closed' || p.paymentRaw === 'cancelled') ? 'co-pay-muted' : 'co-pay-open';
    const due = p.dueDate ? `Vade: ${escHtml(p.dueDate)}${p.daysOverdue ? ` · <b>${p.daysOverdue}g gecikmiş</b>` : ''}` : '';
    return `<div class="co-buy-item">
      <div class="co-buy-body">
        <div class="co-buy-title">${escHtml(p.productName)}</div>
        <div class="co-buy-meta">Alım: ${escHtml(p.purchasedAt || '—')}${due ? ' · ' + due : ''}</div>
      </div>
      <div class="co-buy-right">
        <div class="co-buy-amt">${nf(p.amount)} ${escHtml(p.currency)}</div>
        <span class="co-pay ${cls}">${escHtml(p.paymentStatus)}</span>
      </div>
    </div>`;
  }).join('');
  return `<div class="gb-card"><div class="gb-card-title">🛒 Ürün & Satın Alımlarınız <span class="gb-card-sub">alım · ödeme · vade</span></div><div class="co-buy-list">${rows}</div></div>`;
}
// Teklif satırları — hem müşteri panelindeki "Teklif & Fırsatlarınız" kartı hem
// teklif aşamasındaki panel aynı biçimi kullanır (tek görsel dil).
function teklifSatirlari(items: ClientQuote[]): string {
  return items.slice(0, 20).map((q) => {
    const cls = (q.stageRaw === 'WON' || q.stageRaw === 'ACCEPTED') ? 'co-q-won'
      : (q.stageRaw === 'PROPOSAL' || q.stageRaw === 'SENT' || q.stageRaw === 'NEGOTIATION') ? 'co-q-prop' : 'co-q-new';
    const amt = q.amount ? `<span class="co-q-amt">${nf(q.amount)} ${escHtml(q.currency)}</span>` : '';
    const na = q.nextAction ? `<div class="co-q-next">Sıradaki: ${escHtml(q.nextAction)}</div>` : '';
    return `<div class="co-q-item">
      <div class="co-q-body"><div class="co-q-title">${escHtml(q.title)}</div>${na}</div>
      <div class="co-q-right">${amt}<span class="co-q-stage ${cls}">${escHtml(q.stage)}</span></div>
    </div>`;
  }).join('');
}
function quotesCard(list: ClientQuote[] | null | undefined): string {
  const items = Array.isArray(list) ? list : [];
  if (!items.length) return '';
  return `<div class="gb-card"><div class="gb-card-title">📝 Teklif & Fırsatlarınız <span class="gb-card-sub">görüşme · teklif · onay</span></div><div class="co-q-list">${teklifSatirlari(items)}</div></div>`;
}

// ── K5/K6 — TEKLİF AŞAMASINDAKİ PANEL ───────────────────────────────────────
// Müşteri panelinden ayrı bir görünüm. Gösterilenler: teklifin kendisi, ne
// konuştuğumuz, varsa ücretsiz ön analiz, sonraki adım.
//
// GÖSTERİLMEYENLER ve sebebi: hizmet kartları, finans/bakiye, aylık rapor,
// reklam karnesi, iş adımları, teslimatlar. Bunların hiçbiri teklif aşamasında
// mevcut değil; çizilirlerse hepsi boş çıkar. 14 portalın 12'sine hiç
// girilmemişti, 8'i boştu — boş panel bağlantı olmamasından kötüdür.
function tkGorusmeKarti(g: TeklifGorusme | null): string {
  if (!g) return '';
  const satir: string[] = [];
  if (g.ilkTemas) satir.push(`İlk temasımız: <strong>${escHtml(g.ilkTemas)}</strong>`);
  if (g.kanal) satir.push(`Kanal: <strong>${escHtml(g.kanal)}</strong>`);
  if (!satir.length && !g.ozet) return '';
  const ust = satir.length ? `<div class="co-tk-meta">${satir.join(' · ')}</div>` : '';
  // Özet YOKSA uydurulmaz; yalnız tarih/kanal gösterilir.
  const ozet = g.ozet
    ? `<div class="co-tk-text">${escHtml(g.ozet)}</div>`
    : `<div class="gb-empty-sm">Görüşmemizin özeti teklifle birlikte burada yer alacak.</div>`;
  return `<div class="gb-card"><div class="gb-card-title">💬 Görüşmemiz</div>${ust}${ozet}</div>`;
}
function tkAnalizKarti(a: TeklifAnaliz | null): string {
  if (!a) return '';
  const link = a.link
    ? `<div style="margin-top:8px"><a class="co-chip co-chip-link" href="${escAttr(a.link)}" target="_blank" rel="noopener">Analizi aç ↗</a></div>`
    : '';
  return `<div class="gb-card"><div class="gb-card-title">🔍 ${escHtml(a.baslik)} <span class="gb-card-sub">ücretsiz ön çalışma</span></div>
    ${a.ozet ? `<div class="co-tk-text">${escHtml(a.ozet)}</div>` : ''}${link}</div>`;
}
function teklifPaneli(d: Overview, p: TeklifPano): string {
  const name = shortName(d.tenant.display_name);
  const donmus = p.durum === 'frozen';
  const durumEtiket = FUNNEL_LABEL[p.durum] || 'Teklif';

  const header = `<div class="co-header">
    <div class="co-brand"><div class="co-brand-mark" aria-hidden="true">${escHtml(monogram(name))}</div>
      <div class="co-brand-txt"><div class="co-brand-name">${escHtml(name)} <span class="co-chip">Teklif Paneli</span></div>
        <div class="co-brand-sub">Teklif süreciniz — kayıtlarınız burada durur</div></div></div>
    <div class="co-head-meta">
      <span class="co-status-pill${donmus ? '' : ' on'}"><span class="co-status-dot"></span>${escHtml(durumEtiket)}</span>
    </div></div>`;

  // Donmuş teklifte huni çizilmez: süreç ilerlemiyor, ilerliyormuş gibi
  // göstermek yanlış olurdu. Yerine ne olduğunu açıkça yazan bir bant geçer.
  const kapanisTarihi = p.dondurma && p.dondurma.zaman ? escHtml(p.dondurma.zaman) + ' tarihinde kapandı. ' : '';
  const bant = donmus
    ? `<div class="gb-card co-suspend-banner" style="border-left:4px solid var(--gem-amber,#fbbf24)">
        <div class="gb-card-title">Bu teklif süreci kapandı</div>
        <div class="gb-card-sub">${kapanisTarihi}Teklifiniz ve görüşme kaydınız erişiminizde kalmaya devam eder; yeni bir çalışma başlamaz. Yeniden görüşmek isterseniz bize yazmanız yeterli.</div></div>`
    : journeyStrip('proposed');

  const teklifKart = p.teklifler.length
    ? `<div class="gb-card"><div class="gb-card-title">📝 Teklifiniz <span class="gb-card-sub">${p.teklifler.length} kalem</span></div><div class="co-q-list">${teklifSatirlari(p.teklifler)}</div></div>`
    : `<div class="gb-card"><div class="gb-card-title">📝 Teklifiniz</div>
        <div class="gb-empty-sm">Teklifiniz hazırlanıyor. Hazır olduğunda burada görünecek ve size ayrıca iletilecek.</div></div>`;

  const adim = `<div class="gb-card"><div class="gb-card-title">➡️ Sonraki Adım</div>
    <div class="co-tk-text">${escHtml(p.sonrakiAdim)}</div></div>`;

  // İletişim: açık teklifte destek formu (soru/revizyon buradan gelsin).
  // Donmuş teklifte form YOK — süreç kapandı, destek kuyruğuna talep düşmesin.
  const iletisim = donmus
    ? `<div class="gb-card"><div class="gb-card-title">💬 Bize Ulaşın</div>
        <div class="co-tk-text">Bu süreci yeniden açmak veya yeni bir teklif istemek için bizimle iletişime geçebilirsiniz.</div></div>`
    : supportCard(d.support);

  return `${header}${bant}${teklifKart}${tkGorusmeKarti(p.gorusme)}${tkAnalizKarti(p.analiz)}${adim}${iletisim}`;
}
/**
 * Panel gövdesini üretir. SAF: yalnız `d`'den okur, DOM'a yazmaz, ağ çağırmaz.
 *
 * CA-MERKEZI-KAYIT-003-EK3-T139 — `clientOsPanelHtml` adıyla ayrıca dışa açıldı.
 * NEDEN: `work_records_status` uyarısının panelde GERÇEKTEN nerede ve kaç kez
 * çizildiği yalnız kaynak taramasıyla ölçülüyordu; uçtan uca HTML kanıtı yoktu.
 * Dışa aktarım TEST EDİLEBİLİRLİK içindir — çağrı yeri (`renderClientOs`) ve
 * davranış DEĞİŞMEDİ; yeni DOM/ağ erişimi eklenmedi.
 */
export function clientOsPanelHtml(d: Overview): string {
  return html(d);
}

function html(d: Overview): string {
  // Teklif aşaması ayrı bir panel — müşteri panelinin boş hâli DEĞİL.
  if (d.mod === 'teklif' && d.teklif) return teklifPaneli(d, d.teklif);
  const t = d.tenant;
  const name = shortName(t.display_name);
  const isActive = (t.status || '').toLowerCase() === 'active';
  const statusLabel = FUNNEL_LABEL[(t.status || '').toLowerCase()] || t.status || 'Aktif Hizmet';
  // Direction-A header: marka monogramı + başlık + Aktif-Hizmet pill + Client OS chip + alan adı.
  const header = `${portfolioSwitcher()}<div class="co-header">
    <div class="co-brand"><div class="co-brand-mark" aria-hidden="true">${escHtml(monogram(name))}</div>
      <div class="co-brand-txt"><div class="co-brand-name">${escHtml(name)} <span class="co-chip">Müşteri Paneli</span></div>
        <div class="co-brand-sub">${escHtml(t.sector ? ('Sektör: ' + t.sector) : 'Müşteri Portalı')}</div></div></div>
    <div class="co-head-meta">
      ${d.support_since ? `<span class="co-chip" title="Hizmet başlangıcı">🤝 ${escHtml(ymTr(d.support_since))}'ten beri hizmetinizde</span>` : ''}
      <span class="co-status-pill ${isActive ? 'on' : ''}"><span class="co-status-dot"></span>${escHtml(statusLabel)}</span>
      ${t.domain ? `<a class="co-chip co-chip-link" href="https://${escAttr(t.domain)}" target="_blank" rel="noopener">${escHtml(t.domain)} ↗</a>` : ''}
    </div></div>`;

  // P5 — hizmet askıya alındıysa müşteriye nazik bilgilendirme bandı (durdurma yalnız onaylı).
  const suspended = (t.status || '').toLowerCase() === 'paused';
  const suspendBanner = suspended
    ? `<div class="gb-card co-suspend-banner" style="border-left:4px solid var(--gem-amber,#fbbf24)">
        <div class="gb-card-title">⏸️ Hizmetiniz geçici olarak duraklatıldı</div>
        <div class="gb-card-sub">Açık bakiyeniz nedeniyle hizmetleriniz geçici olarak duraklatılmıştır. Ödemeniz tamamlandığında hizmetiniz aynı gün yeniden başlatılır — verileriniz ve raporlarınız korunmaktadır. Detay ve iletişim için bu paneldeki destek bölümünü kullanabilirsiniz.</div></div>`
    : '';

  const journey = journeyStrip(t.status);

  // KPI — müşteri-dostu (iç-terimler Fırsat/Teşhis kaldırıldı).
  const openSupport = (d.support || []).filter(s => s.customer_status !== 'closed').length;
  const kpis = `<div class="gb-kpi-grid gb-kpi-row2">
    ${kpiTile('🧩', 'Hizmet', (Array.isArray(d.services) && d.services.length) ? d.services.length : d.subscriptions.length, 'var(--gem-sapphire,#60a5fa)')}
    ${kpiTile('📁', 'Aktif İş', d.projects.length, 'var(--gem-emerald,#34d399)')}
    ${kpiTile('🎫', 'Açık Destek', openSupport, 'var(--gem-amber,#fbbf24)')}
    ${kpiTile('🛰️', 'Durum', statusLabel, 'var(--gem-teal,#2dd4bf)')}
  </div>`;

  // Anlaşma kapsamı — hizmet-merkezli bölümler.
  // P1: services[] (açıklamalı katalog) varsa v2 kartlar; yoksa eski subscriptions yolu.
  const used = new Set<string>();
  const _grp = (t: string, sub: string, b: string) => b ? `<div class="co-section-title">${t}</div><div class="gb-card-sub" style="margin:-6px 0 10px">${escHtml(sub)}</div>${b}` : '';
  const svcList = (Array.isArray(d.services) ? d.services : []).filter((s) => s && s.title);
  let servicesWrap: string;
  if (svcList.length) {
    const usedAct = new Set<number>();
    const isWeb = (s: ServiceDef) => (s.channels || []).includes('web');
    const _dig2 = svcList.filter(isWeb).map((s) => serviceCardV2(s, d, used, usedAct)).join('');
    const _mkt2 = svcList.filter((s) => !isWeb(s)).map((s) => serviceCardV2(s, d, used, usedAct)).join('');
    servicesWrap = _grp('🌐 Dijital Varlığınız', 'Web siteleriniz ve arama görünürlüğünüz (SEO/AEO/GEO/AIO)', _dig2)
      + _grp('📣 Pazarlama Yönetiminiz', 'Sizin için aktif yürüttüğümüz reklam çalışmaları — hesap hesap', _mkt2);
  } else {
    const DIGITAL_SET = ['web-sitesi','web-management','seo','aeo','aio-geo','analitik','castdesign'];
    const _dig = d.subscriptions.filter((s) => DIGITAL_SET.includes(s)).map((s) => serviceSection(s, d, used)).filter(Boolean).join('');
    const _mkt = d.subscriptions.filter((s) => !DIGITAL_SET.includes(s)).map((s) => serviceSection(s, d, used)).filter(Boolean).join('');
    servicesWrap = _grp('🌐 Dijital Varlığınız', 'Kurulan ve teslim edilen dijital altyapınız', _dig) + _grp('📣 Pazarlama Yönetiminiz', 'Sizin için aktif yürüttüğümüz reklam çalışmaları', _mkt);
  }
  // İki yol da (hizmet kataloğu v2 + eski subscriptions) aynı uyarıyı kapsar;
  // kart başına TEKRARLANMAZ — bölümün başında bir kez çizilir.
  servicesWrap = workRecordsNotice(d.work_records_status) + servicesWrap;
  const monthlyCard = monthlyReportCard(d.monthly_report, d.tenant.slug, d.report_pdf);
  const otherProjs = d.projects.filter((p) => !used.has(p.slug));
  const otherWork = otherProjs.length ? projectsCard(otherProjs) : '';

  const finance = d.finance ? financeCard(d.finance) : '';
  const mirror = (d.mirror && d.mirror.mapped) ? mirrorCard(d.mirror) : financeFramework();
  const support = supportCard(d.support);
  const mesajlar = coMsgThread ? mesajAkisiHtml(coMsgThread.konusma, coMsgThread.mesajlar, cokMarkali(d.mesajlar)) : mesajlarKartiHtml(d.mesajlar, coMsgHata);
  const sosyalMedya = firmaOzetiKartiHtml(d.sosyalMedyaOzeti);
  const satisfaction = satisfactionCard(d.satisfaction);
  const upcoming = upcomingCard(d);
  const agent = `<div class="gb-card co-gm-card">
    <div class="co-gm-head">
      <div class="co-gm-avatar" aria-hidden="true">🛰️</div>
      <div class="co-gm-id"><div class="co-gm-name">Hesap Yöneticiniz</div>
        <div class="co-gm-role">${escHtml(name)} — GM (Genel Müdür) ekibiniz</div></div>
    </div>
    <div class="co-gm-note">İşlerinizi planlar, yürütür ve sürecinizi uçtan uca yönetir. Durum, rapor veya isteğinizi iletin — talebiniz doğrudan ekibinize düşer. Fiyat/tarih/kapsam taahhüdü insan onayından geçer.</div>
    <div class="co-gm-actions">
      <button class="gb-meeting-btn" onclick="(document.getElementById('co-sup-subject')||{focus:function(){}}).focus();(document.getElementById('co-sup-subject')||{}).scrollIntoView&&document.getElementById('co-sup-subject').scrollIntoView({behavior:'smooth',block:'center'})">💬 Mesaj Gönder</button>
    </div></div>`;

  const accountLog = accountLogCard(d.account_log);
  const tasks = tasksCard(d.tasks);
  const deliveries = deliveriesCard(d.deliveries);
  const quotes = quotesCard(d.quotes);
  const purchases = purchasesCard(d.purchases);
  const routine = routineServicesCard(d.routine_services);
  return `${header}${suspendBanner}${journey}${kpis}${upcoming}${quotes}${tasks}${deliveries}${purchases}${servicesWrap}${routine}${monthlyCard}${otherWork}${finance}${mirror}${sosyalMedya}${mesajlar}${support}${satisfaction}${accountLog}${agent}`;
}
